import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Flex, Text, Button, TextField, Box, IconButton, Separator, Tabs, Select, Tooltip, Card, TextArea, Switch, Badge, 
  Dialog, AlertDialog } from "@radix-ui/themes";
import { ArrowLeftIcon, PlusIcon, TrashIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, ChevronDownIcon, ChevronUpIcon, 
  Cross2Icon, DragHandleDots2Icon, CheckIcon, Pencil1Icon, ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { ComputerDesktopIcon, DevicePhoneMobileIcon } from "@heroicons/react/16/solid";
import { UploadIcon } from "@radix-ui/react-icons";
import { ToastAlert } from "components";
import { useProfileContext, useLocationContext } from "context";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { useGetAllBrands } from "hooks";
import { useSurveyTemplates, useSurveyTemplateDetails } from "hooks/bSurveys/useSurveyTemplates";
import { useRewardCodes, useRewardCampaign } from "hooks/bSurveys/useRewards";
import { DatePickerWithRange } from "components/datepicker/DatePickerWithRange";

// Utility function to convert hex to rgba
const colorConversion = (inputColour, opacity) => {
  if (!inputColour || opacity === undefined) return "rgba(0, 0, 0, 0)";
  let hex = inputColour.replace("#", "");
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
};

const parseLocalDateString = (dateString) => {
  if (!dateString) return null;

  if (dateString instanceof Date) {
    if (Number.isNaN(dateString.getTime())) return null;
    return new Date(dateString.getFullYear(), dateString.getMonth(), dateString.getDate());
  }

  const dateText = String(dateString);
  const ymdMatch = dateText.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymdMatch) {
    const [, yearText, monthText, dayText] = ymdMatch;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(dateText);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const formatLocalDateString = (dateValue) => {
  const parsedDate = parseLocalDateString(dateValue);
  if (!parsedDate) return null;
  const year = parsedDate.getFullYear();
  const month = `${parsedDate.getMonth() + 1}`.padStart(2, '0');
  const day = `${parsedDate.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDaysLocal = (dateValue, dayDelta) => {
  const parsedDate = parseLocalDateString(dateValue);
  if (!parsedDate) return null;
  const shifted = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
  shifted.setDate(shifted.getDate() + dayDelta);
  return shifted;
};

const serializeRewardsConfig = (config) => JSON.stringify({
  enabled: Boolean(config?.enabled),
  runUntilLimitReached: Boolean(config?.runUntilLimitReached),
  dateRange: {
    from: formatLocalDateString(config?.dateRange?.from),
    to: formatLocalDateString(config?.dateRange?.to),
  },
});

const deserializeRewardsConfig = (value) => {
  if (!value) {
    return {
      enabled: false,
      dateRange: { from: null, to: null },
      runUntilLimitReached: false,
    };
  }

  const source = typeof value === 'string' ? JSON.parse(value) : value;

  return {
    enabled: Boolean(source?.enabled),
    dateRange: {
      from: parseLocalDateString(source?.dateRange?.from),
      to: parseLocalDateString(source?.dateRange?.to),
    },
    runUntilLimitReached: Boolean(source?.runUntilLimitReached),
  };
};

const REWARD_PROMO_TEXT = 'Complete our survey and receive a special reward!**';

const isSvgFile = (file) => {
  if (!file) return false;
  const fileName = String(file.name || '').toLowerCase();
  const mimeType = String(file.type || '').toLowerCase();
  return fileName.endsWith('.svg') || fileName.endsWith('.svgz') || mimeType === 'image/svg+xml';
};

// Form block types configuration
// NOTE: 'email' is a SYSTEM block - it's auto-created for every template,
// always first (order=0), always required, and locked. Users cannot add,
// delete, or reorder it. They can only edit the question text.
const FORM_BLOCK_TYPES = {
  // System blocks - not user-addable, but needed for rendering
  email: {
    name: "Email Address",
    description: "Collect respondent's email (required, always first)",
    icon: "✉️",
    locked: true,  // Cannot be moved or deleted
    system: true,  // Not user-addable
  },
  marketingConsent: {
    name: "Marketing Consent",
    description: "Ask for marketing email consent (required, always last)",
    icon: "📩",
    locked: true,
    system: true,
  },
  // User-addable blocks
  rating: {
    name: "Rating",
    description: "Rate from 1-5 stars",
    icon: "⭐",
  },
  multipleChoice: {
    name: "Multiple Choice",
    description: "Select one option from a list",
    icon: "○",
    hasOptions: true,
  },
  checkbox: {
    name: "Checkbox",
    description: "Select multiple options",
    icon: "☐",
    hasOptions: true,
  },
  shortAnswer: {
    name: "Short Answer",
    description: "Single line text input",
    icon: "—",
  },
  yesNo: {
    name: "Yes/No",
    description: "Simple yes or no question",
    icon: "✓✗",
  },
  howDidYouFindUs: {
    name: "How Did You Find Us",
    description: "Preset discovery source options",
    icon: "🔍",
    preset: true,
    hasOptions: true,
  },
  experienceRating: {
    name: "Experience Rating",
    description: "Rate your overall experience",
    icon: "😊",
    preset: true,
  },
  recommendToFriend: {
    name: "Would Recommend",
    description: "Yes/No recommendation question",
    icon: "👥",
    preset: true,
  },
  whatCanWeImprove: {
    name: "What Can We Improve",
    description: "Open feedback for improvements",
    icon: "💡",
    preset: true,
  },
};

// Block types that users can add from the "Add Block" menu
// Excludes system blocks like 'email'
const USER_ADDABLE_BLOCK_TYPES = Object.entries(FORM_BLOCK_TYPES)
  .filter(([_, config]) => !config.system)
  .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

// Default form blocks for new templates - email is always first and locked,
// marketingConsent is always last and locked
const DEFAULT_FORM_BLOCKS = [
  {
    id: "block-email",
    type: "email",
    question: "What is your email address?",
    required: true,
    locked: true,  // Cannot be moved or deleted
  },
  {
    id: "block-1",
    type: "experienceRating",
    question: "How would you rate your overall experience?",
    required: true,
    locked: false,
  },
  {
    id: "block-2", 
    type: "howDidYouFindUs",
    question: "How did you hear about us?",
    required: false,
    locked: false,
    options: ["Google", "Facebook", "Instagram", "Friend/Family", "Walk-by", "Other"],
  },
  {
    id: "block-marketing-consent",
    type: "marketingConsent",
    question: "Would you like to receive marketing emails (discounts, flash deals, newsletter, etc.) in the future?",
    required: true,
    locked: true,  // Cannot be moved or deleted
  },
];

export default function SurveyTemplateEditor() {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams();
  const { userProfile } = useProfileContext();
  const { locationsData } = useLocationContext();
  const { appearance } = useTheme();
  const { setToastMessage } = useToast();
  
  // Fetch all brands for this organisation
  const { data: brandsData } = useGetAllBrands(userProfile?.organisation_id);
  
  // Survey templates API hooks - only need update for editor (create is done in SurveyTemplates)
  const { 
    updateTemplateAsync, 
    isUpdating 
  } = useSurveyTemplates(userProfile?.organisation_id);
  
  // Fetch template details if editing existing template
  const { data: existingTemplate, isLoading: isLoadingTemplate } = useSurveyTemplateDetails(templateId);
  
  // Rewards API hooks
  const {
    rewardCodes: backendRewardCodes,
    isLoading: isLoadingRewards,
    refetch: refetchRewardCodes,
  } = useRewardCodes(templateId);
  
  const {
    campaign: backendCampaign,
    isLoading: isLoadingCampaign,
  } = useRewardCampaign(templateId);
  
  // State to hold filtered data
  const [brands, setBrands] = useState([]);
  const [independentLocations, setIndependentLocations] = useState([]);

  // Process the data when it's loaded
  useEffect(() => {
    if (brandsData && locationsData) {
      const defaultBrandItem = brandsData.find((brand) => brand.is_default);
      const nonDefaultBrands = brandsData.filter((brand) => !brand.is_default);
      setBrands(nonDefaultBrands);
      const defaultLocations = locationsData.filter((location) => location.brand_id === defaultBrandItem?.id || location.brand_id === null);
      setIndependentLocations(defaultLocations);
    }
  }, [brandsData, locationsData]);

  const [view, setView] = useState("mobile");
  const [viewTransitioning, setViewTransitioning] = useState(false);
  
  // Handle view switch with crossfade to avoid content "spasm"
  const handleViewSwitch = () => {
    setViewTransitioning(true);
    setTimeout(() => {
      setView(view === "mobile" ? "desktop" : "mobile");
      setTimeout(() => {
        setViewTransitioning(false);
      }, 50); // Small delay to let the new layout render before fading in
    }, 150); // Fade out duration
  };
  
  const isNewTemplate = templateId === 'new';
  const templateData = location.state?.template;
  const [isInitialized, setIsInitialized] = useState(false);
  const [discardAlertOpen, setDiscardAlertOpen] = useState(false);
  const [backAlertOpen, setBackAlertOpen] = useState(false);
  const hasHistoryGuardRef = useRef(false);
  const browserBackPendingRef = useRef(false);
  const allowBrowserNavigationRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);
  const placeholderTextColor = appearance === 'dark' ? '#64748b' : 'var(--gray-9)';

  // Form state
  const [formData, setFormData] = useState({
    name: templateData?.name || "New Survey",
    assignedTo: templateData?.assignedTo || templateData?.assigned_to || "unspecified",
    // Intro page
    introTitle: "Share Your Experience",
    introSubtitle: "Tell us about your visit!",
    introTimeEstimate: "Less than a minute",
    // Thank you page
    thankYouMessage: "Thank you for your feedback!",
    // Branding
    logoUrl: "",
    backgroundImageUrl: "",
    backgroundOverlayColor: "#000000",
    backgroundOverlayOpacity: 50,
    backgroundBlurAmount: 0,
    primaryColor: "#0066cc",
    textColor: "#ffffff",
  });
  

  // Rewards state (separate from formData for easier management)
  const [rewardsConfig, setRewardsConfig] = useState({
    enabled: false,
    dateRange: { from: null, to: null },
    runUntilLimitReached: false,
  });
  
  // Track if rewards have been initialized from backend
  const [rewardsInitialized, setRewardsInitialized] = useState(false);
  
  const [rewards, setRewards] = useState([]);
  
  // Track original rewards for change detection
  const [originalRewards, setOriginalRewards] = useState(null);
  const [originalRewardsConfig, setOriginalRewardsConfig] = useState(null);

  // Initialize rewards from backend data when loaded
  useEffect(() => {
    if (!rewardsInitialized && !isLoadingRewards && !isLoadingCampaign && templateId && templateId !== 'new') {
      // Set reward campaign config
      const campaignConfig = {
        enabled: backendCampaign?.enabled || false,
        dateRange: {
          from: parseLocalDateString(backendCampaign?.start_date),
          to: backendCampaign?.end_date ? addDaysLocal(parseLocalDateString(backendCampaign.end_date), -1) : null,
        },
        runUntilLimitReached: backendCampaign?.run_until_limit_reached || false,
      };
      setRewardsConfig(campaignConfig);
      setOriginalRewardsConfig(serializeRewardsConfig(campaignConfig));
      
      // Set reward codes
      let frontendRewards = [];
      if (backendRewardCodes && backendRewardCodes.length > 0) {
        frontendRewards = backendRewardCodes.map((reward) => ({
          id: reward.id,
          title: reward.title,
          probability: reward.probability,
          stockLimit: reward.stock_limit,
          stockUsed: reward.stock_used,
          couponCode: reward.coupon_code,
          description: reward.description || '',
          terms: reward.terms || '',
          wheelColor: reward.wheel_color || '',
        }));
        setRewards(frontendRewards);
      }
      setOriginalRewards(JSON.stringify(frontendRewards));
      
      setRewardsInitialized(true);
    }
  }, [backendRewardCodes, backendCampaign, isLoadingRewards, isLoadingCampaign, rewardsInitialized, templateId]);
  
  // Reward editing dialog state
  const [editingReward, setEditingReward] = useState(null);
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [isNewReward, setIsNewReward] = useState(false);  // Track if we're creating a new reward
  const [rewardDeleteConfirmOpen, setRewardDeleteConfirmOpen] = useState(false);
  const [rewardToDelete, setRewardToDelete] = useState(null);
  
  // Preview wheel state
  const [showWheelPreview, setShowWheelPreview] = useState(false);
  const [wheelSpinning, setWheelSpinning] = useState(false);
  const [wheelResult, setWheelResult] = useState(null);
  const [showRewardPopup, setShowRewardPopup] = useState(false);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [hasCompletedRewardFlow, setHasCompletedRewardFlow] = useState(false);

  // Generate vibrant wheel colors based on primary color
  const generateWheelColors = (primaryColor, count) => {
    // Parse the primary color
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Convert to HSL for easier manipulation
    const max = Math.max(r, g, b) / 255;
    const min = Math.min(r, g, b) / 255;
    const l = (max + min) / 2;
    let h, s;
    
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r / 255: h = ((g / 255 - b / 255) / d + (g < b ? 6 : 0)) / 6; break;
        case g / 255: h = ((b / 255 - r / 255) / d + 2) / 6; break;
        case b / 255: h = ((r / 255 - g / 255) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    
    // Generate vibrant colors by rotating hue
    const colors = [];
    for (let i = 0; i < count; i++) {
      const hue = (h + (i / count)) % 1;
      const saturation = Math.min(0.85, s + 0.2); // Boost saturation for vibrancy
      const lightness = 0.5; // Keep lightness at 50% for vibrant colors
      
      // HSL to RGB
      const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
      const x = c * (1 - Math.abs((hue * 6) % 2 - 1));
      const m = lightness - c / 2;
      let r1, g1, b1;
      
      if (hue < 1/6) { r1 = c; g1 = x; b1 = 0; }
      else if (hue < 2/6) { r1 = x; g1 = c; b1 = 0; }
      else if (hue < 3/6) { r1 = 0; g1 = c; b1 = x; }
      else if (hue < 4/6) { r1 = 0; g1 = x; b1 = c; }
      else if (hue < 5/6) { r1 = x; g1 = 0; b1 = c; }
      else { r1 = c; g1 = 0; b1 = x; }
      
      const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
      colors.push(`#${toHex(r1)}${toHex(g1)}${toHex(b1)}`);
    }
    return colors;
  };

  // Track original form data for proper change detection
  const [originalFormData, setOriginalFormData] = useState(null);
  const [originalFormBlocks, setOriginalFormBlocks] = useState(null);

  const [formBlocks, setFormBlocks] = useState(templateData?.formBlocks || templateData?.form_blocks || DEFAULT_FORM_BLOCKS);
  
  // Initialize form with existing template data when loaded
  useEffect(() => {
    if (existingTemplate && !isInitialized) {
      const initialFormData = {
        name: existingTemplate.name || "New Survey",
        assignedTo: existingTemplate.assigned_to || "unspecified",
        introTitle: existingTemplate.intro_title || "Share Your Experience",
        introSubtitle: existingTemplate.intro_subtitle || "Tell us about your visit!",
        introTimeEstimate: existingTemplate.intro_time_estimate || "Less than a minute",
        thankYouMessage: existingTemplate.thank_you_message || "Thank you for your feedback!",
        logoUrl: existingTemplate.logo_url || "",
        backgroundImageUrl: existingTemplate.background_image_url || "",
        backgroundOverlayColor: existingTemplate.background_overlay_color || "#000000",
        backgroundOverlayOpacity: existingTemplate.background_overlay_opacity ?? 50,
        backgroundBlurAmount: existingTemplate.background_blur_amount ?? 0,
        primaryColor: existingTemplate.primary_color || "#0066cc",
        textColor: existingTemplate.text_color || "#ffffff",
      };
      setFormData(initialFormData);
      setOriginalFormData(initialFormData);
      
      // Convert API form blocks to frontend format
      const blocks = (existingTemplate.form_blocks || []).map((block, index) => ({
        id: block.id ? `block-${block.id}` : `block-${index}`,
        type: block.block_type,
        question: block.question,
        required: block.is_required,
        locked: block.is_locked || block.block_type === 'email' || block.block_type === 'marketingConsent',  // System blocks are always locked
        options: block.options?.map(opt => opt.text) || undefined,
      }));
      
      // Ensure email block exists and is first
      const hasEmailBlock = blocks.some(b => b.type === 'email');
      if (!hasEmailBlock) {
        blocks.unshift({
          id: 'block-email',
          type: 'email',
          question: 'What is your email address?',
          required: true,
          locked: true,
        });
      }
      
      // Ensure marketingConsent block exists and is last
      const hasMarketingConsentBlock = blocks.some(b => b.type === 'marketingConsent');
      if (!hasMarketingConsentBlock) {
        blocks.push({
          id: 'block-marketing-consent',
          type: 'marketingConsent',
          question: 'Would you like to receive marketing emails (discounts, flash deals, newsletter, etc.) in the future?',
          required: true,
          locked: true,
        });
      } else {
        // Ensure it's always last
        const mcIndex = blocks.findIndex(b => b.type === 'marketingConsent');
        if (mcIndex !== blocks.length - 1) {
          const [mcBlock] = blocks.splice(mcIndex, 1);
          blocks.push(mcBlock);
        }
      }
      
      const finalBlocks = blocks.length > 0 ? blocks : DEFAULT_FORM_BLOCKS;
      setFormBlocks(finalBlocks);
      setOriginalFormBlocks(JSON.stringify(finalBlocks));
      
      // Set preview images if they exist (use _url fields from API)
      if (existingTemplate.logo_url) {
        setLogoPreview(existingTemplate.logo_url);
        setLogoFileName('Current logo');
      }
      if (existingTemplate.background_image_url) {
        setBackgroundPreview(existingTemplate.background_image_url);
        setBackgroundFileName('Current background');
      }
      
      setIsInitialized(true);
    }
  }, [existingTemplate, isInitialized]);
  const [expandedBlockId, setExpandedBlockId] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Preview state - current page index (-1 = intro, 0+ = questions, formBlocks.length = thank you)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);

  // Preview form answers state (for interactive preview)
  const [previewAnswers, setPreviewAnswers] = useState({});

  // Update preview answer for a specific block
  const updatePreviewAnswer = (blockId, value) => {
    setPreviewAnswers(prev => ({ ...prev, [blockId]: value }));
  };

  // Image upload state - Logo
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFileName, setLogoFileName] = useState("");
  const [logoFile, setLogoFile] = useState(null);  // Store the actual file
  const [deleteLogo, setDeleteLogo] = useState(false);  // Track if logo should be deleted
  const logoInputRef = useRef(null);

  // Image upload state - Background
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [backgroundFileName, setBackgroundFileName] = useState("");
  const [backgroundFile, setBackgroundFile] = useState(null);  // Store the actual file
  const [deleteBackgroundImage, setDeleteBackgroundImage] = useState(false);  // Track if background should be deleted
  const backgroundInputRef = useRef(null);

  // Proper change detection - compare current state with original
  useEffect(() => {
    // Don't run until we have original data loaded
    if (!originalFormData || !originalFormBlocks) return;
    
    // Check if formData has changed (excluding URL fields which change during preview)
    const formDataChanged = Object.keys(formData).some(key => {
      // Skip URL fields that change during local preview
      if (key === 'logoUrl' || key === 'backgroundImageUrl') return false;
      return formData[key] !== originalFormData[key];
    });
    
    // Check if form blocks have changed
    const formBlocksChanged = JSON.stringify(formBlocks) !== originalFormBlocks;
    
    // Check if we have pending file uploads or deletions
    const hasFileUploads = logoFile !== null || backgroundFile !== null;
    const hasImageDeletions = deleteLogo || deleteBackgroundImage;
    
    // Check if rewards have changed
    const rewardsChanged = originalRewards !== null && JSON.stringify(rewards) !== originalRewards;
    const rewardsConfigChanged = originalRewardsConfig !== null && serializeRewardsConfig(rewardsConfig) !== originalRewardsConfig;
    
    setHasUnsavedChanges(formDataChanged || formBlocksChanged || hasFileUploads || hasImageDeletions || rewardsChanged || rewardsConfigChanged);
  }, [formData, formBlocks, originalFormData, originalFormBlocks, logoFile, backgroundFile, deleteLogo, deleteBackgroundImage, rewards, rewardsConfig, originalRewards, originalRewardsConfig]);

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const handlePopState = () => {
      if (allowBrowserNavigationRef.current) {
        return;
      }

      if (hasUnsavedChangesRef.current) {
        browserBackPendingRef.current = true;
        setBackAlertOpen(true);
        window.history.go(1);
        return;
      }

      if (hasHistoryGuardRef.current) {
        hasHistoryGuardRef.current = false;
        allowBrowserNavigationRef.current = true;
        window.history.back();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    if (!hasUnsavedChanges || hasHistoryGuardRef.current) {
      return undefined;
    }

    window.history.pushState({ surveyTemplateEditorGuard: true }, '', window.location.href);
    hasHistoryGuardRef.current = true;

    return undefined;
  }, [hasUnsavedChanges]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Logo upload handlers
  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (isSvgFile(file)) {
      setToastMessage("SVG files are not supported for survey images. Please upload PNG, JPG, or WEBP.");
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
      return;
    }
    if (file.size > 1048576) {
      setToastMessage("Maximum file size is 1MB");
      return;
    }
    setLogoFileName(file.name);
    setLogoFile(file);  // Store the actual file for upload
    setDeleteLogo(false);  // Clear delete flag when uploading new file
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      updateField('logoUrl', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoClear = () => {
    setLogoPreview(null);
    setLogoFileName("");
    setLogoFile(null);  // Clear stored file
    setDeleteLogo(true);  // Mark logo for deletion
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    updateField('logoUrl', '');
  };

  const triggerLogoInput = () => {
    if (logoInputRef.current) {
      logoInputRef.current.click();
    }
  };

  // Background image upload handlers
  const handleBackgroundUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (isSvgFile(file)) {
      setToastMessage("SVG files are not supported for survey images. Please upload PNG, JPG, or WEBP.");
      if (backgroundInputRef.current) {
        backgroundInputRef.current.value = "";
      }
      return;
    }
    if (file.size > 1048576) {
      setToastMessage("Maximum file size is 1MB");
      return;
    }
    setBackgroundFileName(file.name);
    setBackgroundFile(file);  // Store the actual file for upload
    setDeleteBackgroundImage(false);  // Clear delete flag when uploading new file
    const reader = new FileReader();
    reader.onloadend = () => {
      setBackgroundPreview(reader.result);
      updateField('backgroundImageUrl', reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleBackgroundClear = () => {
    setBackgroundPreview(null);
    setBackgroundFileName("");
    setBackgroundFile(null);  // Clear stored file
    setDeleteBackgroundImage(true);  // Mark background for deletion
    if (backgroundInputRef.current) {
      backgroundInputRef.current.value = "";
    }
    updateField('backgroundImageUrl', '');
  };

  const triggerBackgroundInput = () => {
    if (backgroundInputRef.current) {
      backgroundInputRef.current.click();
    }
  };

  // Form block handlers
  const addFormBlock = (type) => {
    const blockConfig = FORM_BLOCK_TYPES[type];
    
    // Determine question text based on type
    let questionText = "New Question";
    if (blockConfig.preset) {
      // Preset blocks have specific default questions
      const presetQuestions = {
        howDidYouFindUs: "How did you hear about us?",
        experienceRating: "How would you rate your overall experience?",
        recommendToFriend: "Would You Recommend This Shop?",
        whatCanWeImprove: "What can we improve?",
      };
      questionText = presetQuestions[type] || blockConfig.name;
    }
    
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      question: questionText,
      required: false,
      options: type === 'multipleChoice' || type === 'checkbox' || type === 'howDidYouFindUs' 
        ? (type === 'howDidYouFindUs' ? ["Google", "Facebook", "Instagram", "Friend/Family", "Walk-by", "Other"] : ["Option 1", "Option 2"]) 
        : undefined,
    };
    // Insert before the marketingConsent block (always last)
    const mcIndex = formBlocks.findIndex(b => b.type === 'marketingConsent');
    if (mcIndex !== -1) {
      const newBlocks = [...formBlocks];
      newBlocks.splice(mcIndex, 0, newBlock);
      setFormBlocks(newBlocks);
    } else {
      setFormBlocks([...formBlocks, newBlock]);
    }
  };

  const updateFormBlock = (blockId, updates) => {
    setFormBlocks(formBlocks.map(block => 
      block.id === blockId ? { ...block, ...updates } : block
    ));
  };

  const removeFormBlock = (blockId) => {
    // Prevent deleting locked blocks (email)
    const block = formBlocks.find(b => b.id === blockId);
    if (block?.locked) {
      return;
    }
    
    setFormBlocks(formBlocks.filter(block => block.id !== blockId));
    // Reset current index if needed
    if (currentQuestionIndex >= formBlocks.length - 1) {
      setCurrentQuestionIndex(Math.max(0, formBlocks.length - 2));
    }
    // Close expanded if we're deleting the expanded block
    if (expandedBlockId === blockId) {
      setExpandedBlockId(null);
    }
  };

  // Option handlers for multiple choice / checkbox blocks
  const addOptionToBlock = (blockId) => {
    const block = formBlocks.find(b => b.id === blockId);
    if (block && block.options) {
      const newOptions = [...block.options, `Option ${block.options.length + 1}`];
      updateFormBlock(blockId, { options: newOptions });
    }
  };

  const updateOptionInBlock = (blockId, optionIndex, newValue) => {
    const block = formBlocks.find(b => b.id === blockId);
    if (block && block.options) {
      const newOptions = [...block.options];
      newOptions[optionIndex] = newValue;
      updateFormBlock(blockId, { options: newOptions });
    }
  };

  const removeOptionFromBlock = (blockId, optionIndex) => {
    const block = formBlocks.find(b => b.id === blockId);
    if (block && block.options && block.options.length > 1) {
      const newOptions = block.options.filter((_, i) => i !== optionIndex);
      updateFormBlock(blockId, { options: newOptions });
    }
  };

  const toggleBlockExpanded = (blockId) => {
    setExpandedBlockId(expandedBlockId === blockId ? null : blockId);
  };

  // Rewards handlers
  const addReward = () => {
    const newReward = {
      id: `reward-${Date.now()}`,
      title: '',
      probability: 10,
      stockLimit: '',
      stockUsed: 0,
      couponCode: '',
      description: '',
      terms: '',
      wheelColor: '',
    };
    // Don't add to rewards array yet - only when saved
    setEditingReward(newReward);
    setIsNewReward(true);
    setRewardDialogOpen(true);
  };

  const updateReward = (rewardId, updates) => {
    setRewards(rewards.map(r => r.id === rewardId ? { ...r, ...updates } : r));
  };

  const deleteReward = (rewardId) => {
    setRewards(rewards.filter(r => r.id !== rewardId));
  };

  const promptDeleteReward = (reward) => {
    setRewardToDelete(reward);
    setRewardDeleteConfirmOpen(true);
  };

  const confirmDeleteReward = () => {
    if (rewardToDelete) {
      deleteReward(rewardToDelete.id);
    }
    setRewardDeleteConfirmOpen(false);
    setRewardToDelete(null);
  };

  const openEditReward = (reward) => {
    setEditingReward({ ...reward });
    setIsNewReward(false);
    setRewardDialogOpen(true);
  };

  const saveEditingReward = () => {
    if (editingReward) {
      const prob = parseInt(editingReward.probability);
      if (isNaN(prob) || prob < 1 || prob > 100) {
        alert('Probability must be a number between 1 and 100.');
        return;
      }

      const rewardToSave = {
        ...editingReward,
        probability: prob,
      };
      
      if (isNewReward) {
        // Add new reward to array
        setRewards([...rewards, rewardToSave]);
      } else {
        // Update existing reward
        updateReward(editingReward.id, rewardToSave);
      }
      
      setRewardDialogOpen(false);
      setEditingReward(null);
      setIsNewReward(false);
    }
  };

  // Calculate total probability
  const totalProbability = rewards.reduce((sum, r) => sum + r.probability, 0);
  
  // Check if any rewards have stock available
  const availableRewards = rewards.filter(r => r.stockUsed < r.stockLimit);
  const hasAvailableStock = availableRewards.length > 0;

  const normalizeToDayStart = (date) => {
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const today = normalizeToDayStart(new Date());
  const startDate = normalizeToDayStart(rewardsConfig.dateRange?.from);
  const endDate = normalizeToDayStart(rewardsConfig.dateRange?.to);
  const hasDateWindow = Boolean(startDate && endDate);

  // UI date range is inclusive: start <= today <= end
  const isInsideDateWindow = hasDateWindow
    ? (today >= startDate && today <= endDate)
    : false;

  const isCampaignActiveNow = (() => {
    if (!rewardsConfig.enabled) return false;

    if (rewardsConfig.runUntilLimitReached) {
      // Run-until-stock mode should ignore date window constraints.
      return hasAvailableStock;
    }

    // Date window only applies when run-until-stock mode is off.
    if (hasDateWindow && !isInsideDateWindow) return false;

    if (hasDateWindow) {
      return true;
    }

    return false;
  })();
  
  // Check if campaign duration is properly configured
  const hasCampaignDuration = rewardsConfig.runUntilLimitReached || 
    (rewardsConfig.dateRange?.from && rewardsConfig.dateRange?.to);

  const showRewardPromoInIntro = Boolean(rewardsConfig.enabled);
  const previewIntroSubtitle = !showRewardPromoInIntro && formData.introSubtitle === REWARD_PROMO_TEXT
    ? ''
    : formData.introSubtitle;

  const wheelBaseRewards = availableRewards.length > 0 ? availableRewards : rewards;
  const wheelDisplayRewards = wheelBaseRewards.length === 1
    ? [0, 1, 2].map((index) => ({
        ...wheelBaseRewards[0],
        _displayId: `${wheelBaseRewards[0].id}-${index}`,
      }))
    : wheelBaseRewards.map((reward, index) => ({
        ...reward,
        _displayId: `${reward.id}-${index}`,
      }));
  
  // Rewards should only show in preview if campaign is currently active and stock exists
  const shouldShowRewardsInPreview = isCampaignActiveNow && hasAvailableStock;

  // Spin the wheel (mock - select a prize based on probability)
  const spinWheel = () => {
    // Prevent spinning if already spinning OR if we already have a result (waiting for popup)
    if (!hasAvailableStock || wheelSpinning || wheelResult || !wheelDisplayRewards.length) return;
    
    setWheelSpinning(true);
    
    // Calculate weighted random selection
    const totalWeight = availableRewards.reduce((sum, r) => sum + r.probability, 0);
    let random = Math.random() * totalWeight;
    let selected = null;
    
    for (let i = 0; i < availableRewards.length; i++) {
      const reward = availableRewards[i];
      random -= reward.probability;
      if (random <= 0) {
        selected = reward;
        break;
      }
    }
    
    // If somehow we didn't select anything, pick the last available
    if (!selected) {
      selected = availableRewards[availableRewards.length - 1];
    }

    const matchingDisplayIndexes = wheelDisplayRewards
      .map((reward, index) => (String(reward.id) === String(selected.id) ? index : -1))
      .filter((index) => index >= 0);
    const selectedDisplayIndex = matchingDisplayIndexes.length
      ? matchingDisplayIndexes[Math.floor(Math.random() * matchingDisplayIndexes.length)]
      : Math.floor(Math.random() * wheelDisplayRewards.length);
    
    // Calculate the rotation angle to land on the selected segment
    // The pointer is at the top (12 o'clock position, which is -90 degrees or 270 degrees)
    // Each segment takes up (360 / wheelDisplayRewards.length) degrees
    const segmentAngle = 360 / wheelDisplayRewards.length;
    // The segment starts at (index * segmentAngle - 90) degrees
    // We want to land in the middle of the segment
    const targetAngle = selectedDisplayIndex * segmentAngle + segmentAngle / 2;
    // We need to rotate the wheel so that the target segment is at the top (270 degrees from start)
    // Since wheel starts with first segment at -90 (top), and rotates clockwise
    // To land on segment at targetAngle from the -90 start, we need to rotate -(targetAngle) + extra spins
    const baseSpins = 5; // Number of full rotations for effect
    const finalRotation = wheelRotation + baseSpins * 360 + (360 - targetAngle);
    
    setWheelRotation(finalRotation);
    
    // Wait for animation to complete (3 seconds), then set result immediately
    // The result being set prevents re-spinning
    setTimeout(() => {
      setWheelSpinning(false);
      setWheelResult(selected);
      // NOTE: We do NOT update stockUsed here - this is just a preview demo
      setTimeout(() => {
        setHasCompletedRewardFlow(true);
        setShowRewardPopup(true);
      }, 500);
    }, 3000);
  };

  const resetWheelPreview = () => {
    setShowWheelPreview(false);
    setWheelSpinning(false);
    setWheelResult(null);
    setShowRewardPopup(false);
    setWheelRotation(0);
    setHasCompletedRewardFlow(false);
  };

  // Drag and drop reordering - Google Forms style
  const [draggedBlockId, setDraggedBlockId] = useState(null);
  const [dropIndicatorIndex, setDropIndicatorIndex] = useState(null); // Index where the blue line shows

  const handleDragStart = (e, blockId) => {
    // Prevent dragging locked blocks (email)
    const block = formBlocks.find(b => b.id === blockId);
    if (block?.locked) {
      e.preventDefault();
      return;
    }
    
    setDraggedBlockId(blockId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', blockId);
    // Add a slight delay to allow the drag image to be captured
    setTimeout(() => {
      e.target.style.opacity = '0.4';
    }, 0);
  };

  const handleDragOver = (e, blockId, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    if (blockId === draggedBlockId) return;
    
    // Get the mouse Y position relative to the target element
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseY = e.clientY - rect.top;
    const threshold = rect.height / 2;
    
    // Determine if we should show the indicator above or below this block
    const draggedIndex = formBlocks.findIndex(b => b.id === draggedBlockId);
    
    // Count leading locked blocks (e.g., email at start)
    let leadingLockedCount = 0;
    for (let i = 0; i < formBlocks.length; i++) {
      if (formBlocks[i].locked) leadingLockedCount++;
      else break;
    }
    // Count trailing locked blocks (e.g., marketingConsent at end)
    let trailingLockedCount = 0;
    for (let i = formBlocks.length - 1; i >= 0; i--) {
      if (formBlocks[i].locked) trailingLockedCount++;
      else break;
    }
    const maxDropIndex = formBlocks.length - trailingLockedCount;
    
    if (mouseY < threshold) {
      // Mouse is in the top half - show indicator above this block
      // But don't allow dropping before leading locked blocks or after trailing locked blocks
      const newIndex = Math.max(leadingLockedCount, Math.min(maxDropIndex, index));
      setDropIndicatorIndex(newIndex);
    } else {
      // Mouse is in the bottom half - show indicator below this block
      const newIndex = Math.max(leadingLockedCount, Math.min(maxDropIndex, index + 1));
      setDropIndicatorIndex(newIndex);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're leaving the container entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropIndicatorIndex(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    
    if (!draggedBlockId || dropIndicatorIndex === null) {
      setDraggedBlockId(null);
      setDropIndicatorIndex(null);
      return;
    }

    const draggedIndex = formBlocks.findIndex(b => b.id === draggedBlockId);
    if (draggedIndex === -1) return;
    
    // Don't allow dropping if the dragged block is locked
    const draggedBlock = formBlocks[draggedIndex];
    if (draggedBlock?.locked) {
      setDraggedBlockId(null);
      setDropIndicatorIndex(null);
      return;
    }

    // Count leading locked blocks - can't drop before them
    let leadingLockedCount = 0;
    for (let i = 0; i < formBlocks.length; i++) {
      if (formBlocks[i].locked) leadingLockedCount++;
      else break;
    }
    // Count trailing locked blocks - can't drop after them
    let trailingLockedCount = 0;
    for (let i = formBlocks.length - 1; i >= 0; i--) {
      if (formBlocks[i].locked) trailingLockedCount++;
      else break;
    }

    // Calculate the actual target index
    const maxDropIndex = formBlocks.length - trailingLockedCount;
    let targetIndex = Math.max(leadingLockedCount, Math.min(maxDropIndex, dropIndicatorIndex));
    // If dragging down, we need to account for the removed item
    if (draggedIndex < targetIndex) {
      targetIndex -= 1;
    }

    // Don't move if we're dropping in the same position
    if (draggedIndex === targetIndex) {
      setDraggedBlockId(null);
      setDropIndicatorIndex(null);
      return;
    }

    const newBlocks = [...formBlocks];
    const [removed] = newBlocks.splice(draggedIndex, 1);
    newBlocks.splice(targetIndex, 0, removed);

    setFormBlocks(newBlocks);
    setHasUnsavedChanges(true);
    setDraggedBlockId(null);
    setDropIndicatorIndex(null);

    // Update current question index if needed
    if (currentQuestionIndex === draggedIndex) {
      setCurrentQuestionIndex(targetIndex);
    } else if (draggedIndex < currentQuestionIndex && targetIndex >= currentQuestionIndex) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (draggedIndex > currentQuestionIndex && targetIndex <= currentQuestionIndex) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedBlockId(null);
    setDropIndicatorIndex(null);
  };

  const handleContainerDragOver = (e) => {
    e.preventDefault();
  };

  const handleContainerDrop = (e) => {
    handleDrop(e);
  };

  const handleSave = async () => {
    // Don't allow saving new templates from here - they should be created via the Create Template dialog
    if (isNewTemplate) {
      console.error('Cannot save new template from editor - use Create Template dialog');
      return;
    }

    try {
      // Prepare single payload with ALL data (template + campaign + rewards)
      // Backend will validate everything atomically before saving anything
      
      // Prepare form blocks for API
      const apiFormBlocks = formBlocks.map((block, index) => ({
        block_type: block.type,
        question: block.question,
        is_required: block.required,
        is_locked: block.locked || false,
        order: index,
        options: block.options?.map((text, optIndex) => ({ text, order: optIndex })) || [],
      }));

      // Prepare reward codes - include existing IDs for update, omit for new
      const apiRewardCodes = rewards.map((reward, idx) => {
        const rewardData = {
          title: reward.title,
          description: reward.description || '',
          terms: reward.terms || '',
          coupon_code: reward.couponCode || '',
          probability: reward.probability,
          wheel_color: reward.wheelColor || '',
          stock_limit: reward.stockLimit,
          stock_used: reward.stockUsed || 0,
          order: idx,
        };
        // Include ID only if it's an existing reward (number ID from backend)
        if (typeof reward.id === 'number') {
          rewardData.id = reward.id;
        }
        return rewardData;
      });

      // Prepare reward campaign settings
      const apiRewardCampaign = {
        enabled: rewardsConfig.enabled,
        run_until_limit_reached: rewardsConfig.runUntilLimitReached,
        start_date: formatLocalDateString(rewardsConfig.dateRange?.from),
        end_date: rewardsConfig.dateRange?.to
          ? formatLocalDateString(addDaysLocal(rewardsConfig.dateRange.to, 1))
          : null,
      };

      // Build the complete payload
      const payload = {
        name: formData.name,
        assigned_to: formData.assignedTo,
        intro_title: formData.introTitle,
        intro_subtitle: formData.introSubtitle,
        intro_time_estimate: formData.introTimeEstimate,
        thank_you_message: formData.thankYouMessage,
        background_overlay_color: formData.backgroundOverlayColor,
        background_overlay_opacity: formData.backgroundOverlayOpacity,
        background_blur_amount: formData.backgroundBlurAmount,
        primary_color: formData.primaryColor,
        text_color: formData.textColor,
        form_blocks: apiFormBlocks,
        organisation_id: userProfile.organisation_id,
        // Include rewards data in the same payload
        reward_codes: apiRewardCodes,
        reward_campaign: apiRewardCampaign,
      };

      // =========================================================================
      // STEP 3: Send single request to backend
      // Backend validates ALL data atomically - nothing saved if validation fails
      // =========================================================================
      
      const hasFileChanges = logoFile || backgroundFile || deleteLogo || deleteBackgroundImage;
      
      if (hasFileChanges) {
        // Use FormData for file uploads
        const formDataPayload = new FormData();
        
        Object.keys(payload).forEach(key => {
          if (key === 'form_blocks' || key === 'reward_codes' || key === 'reward_campaign') {
            formDataPayload.append(key, JSON.stringify(payload[key]));
          } else {
            formDataPayload.append(key, payload[key]);
          }
        });

        if (logoFile) {
          formDataPayload.append('logo', logoFile);
        } else if (deleteLogo) {
          formDataPayload.append('logo', '');
        }
        
        if (backgroundFile) {
          formDataPayload.append('background_image', backgroundFile);
        } else if (deleteBackgroundImage) {
          formDataPayload.append('background_image', '');
        }

        await updateTemplateAsync({ templateId, data: formDataPayload });
      } else {
        // Use JSON for regular updates
        await updateTemplateAsync({ templateId, data: payload });
      }

      // =========================================================================
      // STEP 4: Success - update local state
      // =========================================================================
      
      // Clear file state after successful save
      setLogoFile(null);
      setBackgroundFile(null);
      setDeleteLogo(false);
      setDeleteBackgroundImage(false);
      
      if (logoFile) {
        setLogoFileName('Current logo');
      }
      if (backgroundFile) {
        setBackgroundFileName('Current background');
      }
      
      // Update original state to match current state (so change detection works correctly)
      setOriginalFormData({ ...formData });
      setOriginalFormBlocks(JSON.stringify(formBlocks));
      setOriginalRewards(JSON.stringify(rewards));
      setOriginalRewardsConfig(serializeRewardsConfig(rewardsConfig));
      
      setHasUnsavedChanges(false);
      
      // Refetch to get updated reward IDs from backend
      refetchRewardCodes();
      setToastMessage('Survey template saved');
      
    } catch (error) {
      // Display the error message from the backend
      // Backend handles all validation and returns safe, user-friendly messages
      const errorMessage = error?.response?.data?.error 
        || 'Failed to save survey template. Please try again.';
      
      setToastMessage(errorMessage);
    }
  };

  const confirmDiscard = () => {
    // Reset image upload states
    setLogoFile(null);
    setBackgroundFile(null);
    setDeleteLogo(false);
    setDeleteBackgroundImage(false);
    
    // Reset preview states to original from existing template
    if (existingTemplate?.logo_url) {
      setLogoPreview(existingTemplate.logo_url);
      setLogoFileName('Current logo');
    } else {
      setLogoPreview(null);
      setLogoFileName('');
    }
    
    if (existingTemplate?.background_image_url) {
      setBackgroundPreview(existingTemplate.background_image_url);
      setBackgroundFileName('Current background');
    } else {
      setBackgroundPreview(null);
      setBackgroundFileName('');
    }
    
    // Clear file inputs
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
    if (backgroundInputRef.current) {
      backgroundInputRef.current.value = '';
    }
    
    // Reset formData and formBlocks to their original values
    if (originalFormData) {
      setFormData({ ...originalFormData });
    }
    if (originalFormBlocks) {
      setFormBlocks(JSON.parse(originalFormBlocks));
    }
    
    // Reset rewards to their original values
    if (originalRewards) {
      setRewards(JSON.parse(originalRewards));
    }
    if (originalRewardsConfig) {
      setRewardsConfig(deserializeRewardsConfig(originalRewardsConfig));
    }
    
    // Reset wheel preview state
    resetWheelPreview();
    
    setHasUnsavedChanges(false);
    setDiscardAlertOpen(false);
  };

  const handleDiscard = () => {
    setDiscardAlertOpen(true);
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      browserBackPendingRef.current = false;
      setBackAlertOpen(true);
    } else {
      navigate('/surveys/templates');
    }
  };

  const confirmBack = () => {
    setBackAlertOpen(false);

    if (browserBackPendingRef.current) {
      browserBackPendingRef.current = false;
      allowBrowserNavigationRef.current = true;
      window.history.go(-2);
      return;
    }

    navigate('/surveys/templates');
  };

  // Get assignment label for display
  const getAssignmentLabel = () => {
    if (formData.assignedTo === "unspecified") return "General Survey";
    if (formData.assignedTo.startsWith("brand-")) {
      const brandId = formData.assignedTo.replace("brand-", "");
      const brand = brands.find(b => String(b.id) === brandId);
      return brand ? `Brand: ${brand.name}` : "Unknown Brand";
    }
    if (formData.assignedTo.startsWith("location-")) {
      const locationId = formData.assignedTo.replace("location-", "");
      const loc = independentLocations.find(l => String(l.id) === locationId);
      return loc ? `Location: ${loc.name}` : "Unknown Location";
    }
    return "General Survey";
  };

  // Preview navigation
  const isOnIntroPage = currentQuestionIndex === -1;
  const isOnThankYouPage = currentQuestionIndex >= formBlocks.length;
  const canGoNext = currentQuestionIndex < formBlocks.length; // Can go to thank you page
  const canGoPrev = currentQuestionIndex > -1; // Can go back to intro page

  const handleNextQuestion = () => {
    if (canGoNext) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevQuestion = () => {
    if (isOnThankYouPage && hasCompletedRewardFlow) {
      setShowWheelPreview(false);
      setShowRewardPopup(false);
      setWheelSpinning(false);
      setWheelResult(null);
      setWheelRotation(0);
      setHasCompletedRewardFlow(false);
      setCurrentQuestionIndex(Math.max(0, formBlocks.length - 1));
      return;
    }

    if (canGoPrev) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  // Get current block for preview
  const currentBlock = formBlocks[currentQuestionIndex];

  // Render a single question block with interactive inputs inside a white card
  const renderQuestionBlock = (block) => {
    if (!block) return null;
    
    const answer = previewAnswers[block.id];
    
    // Card width is responsive: mobile (375px view) uses ~90% width, desktop uses larger fixed width
    // Adjust maxWidth below to control desktop size
    const cardMaxWidth = view === 'mobile' ? '320px' : '480px';
    const previewSurfaceColor = '#ffffff';
    const previewMutedSurfaceColor = '#f8fafc';
    const previewBorderColor = '#d1d5db';
    const previewControlBorderColor = '#cbd5e1';
    const previewTextColor = '#111827';
    const previewMutedTextColor = '#475569';
    const selectedBackgroundColor = `${formData.primaryColor}15`;
    
    return (
      <Flex direction="column" gap="4" align="center" p="4"
        style={{ width: '90%', 
          maxWidth: cardMaxWidth,
          backgroundColor: 'rgba(255,255,255,0.96)',
          borderRadius: 'var(--radius-4)',
          border: `1px solid ${previewBorderColor}`,
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.14)',
        }}
      >
        {/* Question text */}
        <Text 
          size={view === 'mobile' ? '3' : '4'} 
          weight="medium" 
          align="center" 
          style={{ color: previewTextColor }}
        >
          {block.question}
          {block.required && <Text style={{ color: '#ef4444' }}> *</Text>}
        </Text>
        
        {/* Rating/Experience Rating - Number buttons */}
        {(block.type === 'rating' || block.type === 'experienceRating') && (
          <Flex gap="2" justify={view === 'mobile' ? 'between' : 'center'} style={{ width: view === 'mobile' ? '100%' : 'auto', flexWrap: view === 'mobile' ? 'nowrap' : 'wrap' }}>
            {[1, 2, 3, 4, 5].map((num) => (
              <Box 
                key={num}
                onClick={() => updatePreviewAnswer(block.id, num)}
                style={{ 
                  width: view === 'mobile' ? '44px' : '56px', 
                  height: view === 'mobile' ? '44px' : '56px', 
                  borderRadius: 'var(--radius-3)',
                  backgroundColor: answer === num ? formData.primaryColor : previewMutedSurfaceColor,
                  border: `2px solid ${answer === num ? formData.primaryColor : previewControlBorderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <Text size={view === 'mobile' ? '3' : '4'} weight="medium" style={{ color: answer === num ? '#fff' : previewTextColor }}>{num}</Text>
              </Box>
            ))}
          </Flex>
        )}
        
        {/* Multiple Choice / How Did You Find Us */}
        {(block.type === 'multipleChoice' || block.type === 'howDidYouFindUs') && (
          <Flex direction="column" gap="2" style={{ width: '100%' }}>
            {(block.options || ["Option 1", "Option 2"]).map((option, i) => (
              <Flex 
                key={i} 
                gap="3" 
                align="center"
                onClick={() => updatePreviewAnswer(block.id, option)}
                style={{
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-2)',
                  backgroundColor: answer === option ? selectedBackgroundColor : previewMutedSurfaceColor,
                  border: `2px solid ${answer === option ? formData.primaryColor : previewControlBorderColor}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box 
                  style={{ 
                    width: '20px', 
                    height: '20px', 
                    borderRadius: '50%',
                    border: `2px solid ${answer === option ? formData.primaryColor : previewMutedTextColor}`,
                    backgroundColor: answer === option ? formData.primaryColor : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {answer === option && (
                    <Box style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }} />
                  )}
                </Box>
                <Text size="2" style={{ color: previewTextColor }}>{option}</Text>
              </Flex>
            ))}
          </Flex>
        )}
        
        {/* Checkbox */}
        {block.type === 'checkbox' && (
          <Flex direction="column" gap="2" style={{ width: '100%' }}>
            {(block.options || ["Option 1", "Option 2"]).map((option, i) => {
              const checked = Array.isArray(answer) && answer.includes(option);
              return (
                <Flex 
                  key={i} 
                  gap="3" 
                  align="center"
                  onClick={() => {
                    const current = Array.isArray(answer) ? answer : [];
                    if (checked) {
                      updatePreviewAnswer(block.id, current.filter(o => o !== option));
                    } else {
                      updatePreviewAnswer(block.id, [...current, option]);
                    }
                  }}
                  style={{
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-2)',
                    backgroundColor: checked ? selectedBackgroundColor : previewMutedSurfaceColor,
                    border: `2px solid ${checked ? formData.primaryColor : previewControlBorderColor}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box 
                    style={{ 
                      width: '20px', 
                      height: '20px', 
                      borderRadius: 'var(--radius-1)',
                      border: `2px solid ${checked ? formData.primaryColor : previewMutedTextColor}`,
                      backgroundColor: checked ? formData.primaryColor : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {checked && <CheckIcon width={12} height={12} style={{ color: '#fff' }} />}
                  </Box>
                  <Text size="2" style={{ color: previewTextColor }}>{option}</Text>
                </Flex>
              );
            })}
          </Flex>
        )}
        
        {/* Yes/No */}
        {(block.type === 'recommendToFriend' || block.type === 'yesNo' || block.type === 'marketingConsent') && (
          <Flex gap="3" justify="center" style={{ width: '100%' }}>
            <Box 
              onClick={() => updatePreviewAnswer(block.id, true)}
              style={{ 
                flex: 1,
                maxWidth: '140px',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-3)',
                backgroundColor: answer === true ? selectedBackgroundColor : previewMutedSurfaceColor,
                border: `2px solid ${answer === true ? formData.primaryColor : previewControlBorderColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <CheckIcon width={16} height={16} style={{ color: answer === true ? formData.primaryColor : previewMutedTextColor }} />
              <Text size="3" weight="medium" style={{ color: answer === true ? formData.primaryColor : previewTextColor }}>Yes</Text>
            </Box>
            <Box 
              onClick={() => updatePreviewAnswer(block.id, false)}
              style={{ 
                flex: 1,
                maxWidth: '140px',
                padding: 'var(--space-2)',
                borderRadius: 'var(--radius-3)',
                backgroundColor: answer === false ? selectedBackgroundColor : previewMutedSurfaceColor,
                border: `2px solid ${answer === false ? formData.primaryColor : previewControlBorderColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              <Cross2Icon width={16} height={16} style={{ color: answer === false ? formData.primaryColor : previewMutedTextColor }} />
              <Text size="3" weight="medium" style={{ color: answer === false ? formData.primaryColor : previewTextColor }}>No</Text>
            </Box>
          </Flex>
        )}
        
        {/* Short Answer / What Can We Improve */}
        {(block.type === 'shortAnswer' || block.type === 'whatCanWeImprove') && (
          <TextArea 
            size="2"
            placeholder="Share your thoughts..."
            value={answer || ''}
            onChange={(e) => updatePreviewAnswer(block.id, e.target.value)}
            style={{ 
              width: '100%',
              minHeight: '100px',
              backgroundColor: previewSurfaceColor,
              color: previewTextColor,
              border: `1px solid ${previewBorderColor}`,
              boxShadow: 'none',
            }}
          />
        )}
        
        {/* Email */}
        {block.type === 'email' && (
          <TextField.Root 
            size="2"
            type="email"
            placeholder="your.email@example.com"
            value={answer || ''}
            onChange={(e) => updatePreviewAnswer(block.id, e.target.value)}
            style={{
              width: '100%',
              backgroundColor: previewSurfaceColor,
              color: previewTextColor,
              border: `1px solid ${previewBorderColor}`,
              boxShadow: 'none',
            }}
          />
        )}
      </Flex>
    );
  };

  return (
    <>
      <Flex className="survey-template-editor" direction="column" p="5" gap="5" style={{ width: '100%', height: '100%', flex: '1 0 0'}}>
        <style>{`
          .survey-template-editor input::placeholder,
          .survey-template-editor textarea::placeholder {
            color: ${placeholderTextColor};
            opacity: 1;
          }
        `}</style>
        {/* Navigation header */}
        <Flex style={{ justifyContent: "space-between", alignItems: "center", alignSelf: "stretch" }}>
          <Flex gap="4" style={{ alignItems: "center", flex: "1 0 0" }}>
            <IconButton size="2" variant="soft" color="gray" onClick={handleBack}>
              <ArrowLeftIcon width={16} height={16} />
            </IconButton>
            <Flex direction="column" gap="1">
              <Text size="2" weight="medium">{formData.name}</Text>
              <Text size="1" color="gray">{getAssignmentLabel()}</Text>
            </Flex>
          </Flex>
          <Flex gap="2">
            <Tooltip content={`Switch preview to ${view === "mobile" ? "desktop" : "mobile"} view`} side="bottom">
              <IconButton size="2" variant="soft" color="gray" onClick={handleViewSwitch} disabled={viewTransitioning}>
                {view === "mobile" ? <ComputerDesktopIcon width={16} height={16} /> : <DevicePhoneMobileIcon width={16} height={16} />}
              </IconButton>
            </Tooltip>
          </Flex>
        </Flex>
        
        {/* Main Content */}
        <Flex gap="5" style={{ flex: '1 0 0', overflow: 'hidden' }}>
          {/* Left Panel - Editor Sidebar */}
          <Flex style={{ alignItems: "flex-start", alignSelf: "stretch", overflow: "hidden" }} height="100%">
            <Flex width="420px" direction="column"
              style={{ alignItems: "flex-start", alignSelf: "stretch", flex: "1 0 0",
              borderRadius: "var(--radius-4)", height: "100%", border: "1px solid var(--gray-a6)",
              backgroundColor: "var(--color-panel-solid)", overflow: "hidden" }}>
              <Flex direction="column" style={{ alignSelf: "stretch", overflow: "scroll", flex: "1 0 0" }}>
                {/* Tabs */}
                <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
                  <Flex direction="column" style={{ position: "sticky", top: "0", zIndex: "2", 
                    backgroundColor: "var(--color-panel-solid)", overflow: "hidden" }}>
                    <Tabs.List style={{
                      padding: "var(--space-2) var(--space-4) 0px var(--space-4)",
                      borderTopLeftRadius: "var(--radius-4)",
                      borderTopRightRadius: "var(--radius-4)",
                    }}>
                      <Tabs.Trigger value="general">General</Tabs.Trigger>
                      <Tabs.Trigger value="branding">Branding</Tabs.Trigger>
                      <Tabs.Trigger value="form">Form blocks</Tabs.Trigger>
                      <Tabs.Trigger value="rewards">Rewards</Tabs.Trigger>
                    </Tabs.List>
                  </Flex>
                  
                  {/* General Tab */}
                  <Tabs.Content value="general" key="general-tab">
                    <Flex direction="column" gap="4">
                      <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Text size="1" weight="regular" color="gray">
                          Configure the basic settings for your survey template.
                        </Text>
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
                      
                      <Flex direction="column" gap="3">
                        <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Template name <span style={{ color: 'var(--red-9)' }}>*</span></Text>
                          <TextField.Root 
                            placeholder="e.g., Customer Feedback Survey" 
                            value={formData.name}
                            onChange={(e) => updateField('name', e.target.value)}
                            style={{ alignSelf: 'stretch'}}
                          />
                        </Flex>
                        
                        <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Assigned to</Text>
                          <Select.Root 
                            value={formData.assignedTo} 
                            onValueChange={(value) => updateField('assignedTo', value)}
                          >
                            <Select.Trigger style={{ width: '100%' }} />
                            <Select.Content>
                              <Select.Group>
                                <Select.Label>General</Select.Label>
                                <Select.Item value="unspecified">Unspecified</Select.Item>
                              </Select.Group>
                              {brands.length > 0 && (
                                <Select.Group>
                                  <Select.Label>Brands</Select.Label>
                                  {brands.map((brand) => (
                                    <Select.Item key={`brand-${brand.id}`} value={`brand-${brand.id}`}>
                                      {brand.name}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              )}
                              {independentLocations.length > 0 && (
                                <Select.Group>
                                  <Select.Label>Locations</Select.Label>
                                  {independentLocations.map((loc) => (
                                    <Select.Item key={`location-${loc.id}`} value={`location-${loc.id}`}>
                                      {loc.name}
                                    </Select.Item>
                                  ))}
                                </Select.Group>
                              )}
                            </Select.Content>
                          </Select.Root>
                        </Flex>
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px'}}/>
                      
                      {/* Intro Page Section */}
                      <Flex px="4" direction="column" gap="1">
                        <Text size="3" weight="medium" style={{ marginBottom: '8px' }}>Survey intro</Text>
                        
                        <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Title <span style={{ color: 'var(--red-9)' }}>*</span></Text>
                          <TextField.Root
                            size="2"
                            placeholder="Share Your Experience" 
                            value={formData.introTitle}
                            onChange={(e) => updateField('introTitle', e.target.value)}
                            style={{ alignSelf: 'stretch'}}
                          />
                        </Flex>
                        
                        <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", marginTop: '8px' }}>
                          <Text size="2" weight="medium">Subtitle</Text>
                          <TextField.Root
                            size="2"
                            placeholder="Tell us about your visit!" 
                            value={formData.introSubtitle}
                            onChange={(e) => updateField('introSubtitle', e.target.value)}
                            style={{ alignSelf: 'stretch'}}
                          />
                        </Flex>
                        
                        <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch", marginTop: '8px' }}>
                          <Text size="2" weight="medium">Time estimate <span style={{ color: 'var(--red-9)' }}>*</span></Text>
                          <TextField.Root 
                            size="2"
                            placeholder="Less than a minute" 
                            value={formData.introTimeEstimate}
                            onChange={(e) => updateField('introTimeEstimate', e.target.value)}
                            style={{ alignSelf: 'stretch'}}
                          />
                        </Flex>
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px'}}/>
                      
                      {/* Thank You Page Section */}
                      <Flex px="4" pb="4" direction="column" gap="2">
                        <Text size="3" weight="medium">Thank You Page</Text>
                        
                        <Flex gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Message <span style={{ color: 'var(--red-9)' }}>*</span></Text>
                          <TextArea 
                            size="2"
                            placeholder="Thank you for your feedback!" 
                            value={formData.thankYouMessage}
                            onChange={(e) => updateField('thankYouMessage', e.target.value)}
                            style={{ alignSelf: 'stretch', minHeight: '80px' }}
                          />
                        </Flex>
                      </Flex>
                    </Flex>
                  </Tabs.Content>
                  
                  {/* Branding Tab */}
                  <Tabs.Content value="branding" key="branding-tab">
                    <Flex direction="column" gap="4">
                      <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Text size="1" weight="regular" color="gray">
                          Customize the look and feel of your survey.
                        </Text>
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
                      
                      {/* Logo Upload */}
                      <Flex gap="1" px="4" direction="column" style={{ alignSelf: "stretch" }}>
                        <Text size="2" weight="medium">Logo</Text>
                        <Flex gap="2">
                          <input type="file" style={{ display: "none" }} ref={logoInputRef} onChange={handleLogoUpload} accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" />
                          <Button
                            type="button"
                            size="2"
                            variant="surface"
                            color="gray"
                            style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                            onClick={triggerLogoInput}
                          >
                            {logoPreview ? (
                              <Tooltip content="Upload new to replace">
                                <Text>{logoFileName?.length >= 18 ? `${logoFileName.slice(0, 18)}...` : logoFileName}</Text>
                              </Tooltip>
                            ) : (
                              <>
                                <UploadIcon />
                                <Text>Upload</Text>
                              </>
                            )}
                          </Button>
                          {logoPreview && (
                            <Button type="button" size="2" variant="surface" color="gray" onClick={handleLogoClear}>
                              Clear
                            </Button>
                          )}
                        </Flex>
                        <Text size="1" weight="regular" color="gray">
                          Recommended dimensions: 200 x 96 px. Maximum file size: 1 MB.
                        </Text>
                      </Flex>

                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />

                      {/* Background Image & Overlay */}
                      <Flex gap="4" px="4" direction="column" style={{ alignSelf: "stretch" }}>
                        {/* Background Image */}
                        <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Background image</Text>
                          <Flex gap="2" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                            <input
                              type="file"
                              style={{ display: "none" }}
                              ref={backgroundInputRef}
                              onChange={handleBackgroundUpload}
                              accept=".png,.jpg,.jpeg,.gif,.webp,image/png,image/jpeg,image/gif,image/webp"
                            />
                            <Button
                              type="button"
                              size="2"
                              variant="surface"
                              color="gray"
                              style={{ display: "flex", padding: "0px var(--space-2)", justifyContent: "center", flex: "1" }}
                              onClick={triggerBackgroundInput}
                            >
                              {backgroundPreview ? (
                                <Tooltip content="Upload new to replace">
                                  <Text>{backgroundFileName?.length >= 18 ? `${backgroundFileName.slice(0, 18)}...` : backgroundFileName}</Text>
                                </Tooltip>
                              ) : (
                                <>
                                  <UploadIcon />
                                  <Text>Upload</Text>
                                </>
                              )}
                            </Button>
                            {backgroundPreview && (
                              <Button type="button" size="2" variant="surface" color="gray" onClick={handleBackgroundClear}>
                                Clear
                              </Button>
                            )}
                          </Flex>
                          <Text size="1" weight="regular" color="gray">
                            Recommended dimensions: 900 x 600 px. Maximum file size: 1 MB.
                          </Text>
                        </Flex>
                        
                        {/* Background Overlay */}
                        <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Background overlay</Text>
                          <Flex gap="2" style={{ alignSelf: "stretch", alignItems: "flex-start" }}>
                            <label htmlFor="background_overlay_colour_picker" style={{ position: "relative", display: "inline-block", width: "100%" }}>
                              <input
                                type="color"
                                id="background_overlay_colour_picker"
                                value={formData.backgroundOverlayColor}
                                onChange={(e) => updateField('backgroundOverlayColor', e.target.value)}
                                style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0, cursor: "pointer" }}
                              />
                              <div
                                style={{
                                  width: "100%",
                                  height: "32px",
                                  display: "flex",
                                  backgroundColor: colorConversion(formData.backgroundOverlayColor, formData.backgroundOverlayOpacity),
                                  borderRadius: "var(--radius-2)",
                                  border: "1px solid var(--gray-a6)",
                                }}
                              ></div>
                            </label>
                            <TextField.Root
                              size="2"
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              value={formData.backgroundOverlayOpacity}
                              onChange={(e) => updateField('backgroundOverlayOpacity', parseInt(e.target.value) || 0)}
                              style={{ textAlign: "center", width: "70px" }}
                            >
                              <TextField.Slot side="right" style={{ padding: "4px" }}>
                                %
                              </TextField.Slot>
                            </TextField.Root>
                          </Flex>
                          <Text size="1" weight="regular" color="gray">
                            Recommended for logo visibility.
                          </Text>
                        </Flex>

                        {/* Background Blur */}
                        <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Background blur</Text>
                          <Flex gap="2" style={{ alignSelf: "stretch", alignItems: "center" }}>
                            <input
                              type="range"
                              min={0}
                              max={100}
                              step={1}
                              value={formData.backgroundBlurAmount}
                              onChange={(e) => updateField('backgroundBlurAmount', parseInt(e.target.value) || 0)}
                              style={{ flex: 1 }}
                            />
                            <TextField.Root
                              size="2"
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              value={formData.backgroundBlurAmount}
                              onChange={(e) => updateField('backgroundBlurAmount', parseInt(e.target.value) || 0)}
                              style={{ textAlign: "center", width: "70px" }}
                            >
                              <TextField.Slot side="right" style={{ padding: "4px" }}>
                                %
                              </TextField.Slot>
                            </TextField.Root>
                          </Flex>
                          <Text size="1" weight="regular" color="gray">
                            Applies a blur effect to the background image.
                          </Text>
                        </Flex>
                      </Flex>

                      <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                        
                      {/* Colors */}
                      <Flex gap="4" px="4" pb="4" direction="column" style={{ alignSelf: "stretch" }}>
                        <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Primary colour</Text>
                          <label style={{ position: "relative", display: "inline-block", width: "100%" }}>
                            <input
                              type="color"
                              value={formData.primaryColor}
                              onChange={(e) => updateField('primaryColor', e.target.value)}
                              style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0, cursor: "pointer" }}
                            />
                            <div style={{ width: "100%", height: "32px", backgroundColor: formData.primaryColor, borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}></div>
                          </label>
                          <Text size="1" weight="regular" color="gray">
                            Used for buttons and accents.
                          </Text>
                        </Flex>
                        
                        <Flex gap="1" direction="column" style={{ alignSelf: "stretch" }}>
                          <Text size="2" weight="medium">Text colour</Text>
                          <label style={{ position: "relative", display: "inline-block", width: "100%" }}>
                            <input
                              type="color"
                              value={formData.textColor}
                              onChange={(e) => updateField('textColor', e.target.value)}
                              style={{ opacity: 0, position: "absolute", width: "100%", height: "100%", border: "none", padding: 0, margin: 0, cursor: "pointer" }}
                            />
                            <div style={{ width: "100%", height: "32px", backgroundColor: formData.textColor, borderRadius: "var(--radius-2)", border: "1px solid var(--gray-a6)" }}></div>
                          </label>
                          <Text size="1" weight="regular" color="gray">
                            Main text colour for questions.
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Tabs.Content>
                  
                  {/* Form Blocks Tab */}
                  <Tabs.Content value="form" key="form-tab">
                    <Flex direction="column" gap="4">
                      <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Text size="1" weight="regular" color="gray">
                          Add and arrange form blocks to build your survey.
                        </Text>
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
                      
                      {/* Current Form Blocks */}
                      <Flex 
                        px="4" 
                        direction="column" 
                        gap="0"
                        onDragOver={handleContainerDragOver}
                        onDrop={handleContainerDrop}
                      >
                        <Text size="2" weight="medium" style={{ marginBottom: '8px' }}>Current blocks ({formBlocks.length})</Text>
                        {formBlocks.map((block, index) => {
                          const blockConfig = FORM_BLOCK_TYPES[block.type];
                          const isExpanded = expandedBlockId === block.id;
                          const hasOptions = blockConfig?.hasOptions && block.options;
                          const isActive = currentQuestionIndex === index;
                          const isDragging = draggedBlockId === block.id;
                          const isLocked = block.locked || blockConfig?.locked;
                          const showDropIndicatorAbove = dropIndicatorIndex === index && !isLocked;
                          const showDropIndicatorBelow = dropIndicatorIndex === index + 1 && index === formBlocks.length - 1;
                          
                          return (
                            <Box 
                              key={block.id} 
                              style={{ position: 'relative' }}
                              onDragOver={(e) => handleDragOver(e, block.id, index)}
                              onDragLeave={handleDragLeave}
                            >
                              {/* Drop indicator line - above (not for locked blocks) */}
                              {showDropIndicatorAbove && (
                                <Box
                                  style={{
                                    position: 'absolute',
                                    top: '-1px',
                                    left: 0,
                                    right: 0,
                                    height: '3px',
                                    backgroundColor: '#4285f4',
                                    borderRadius: '2px',
                                    zIndex: 10,
                                  }}
                                />
                              )}
                              
                              {/* Block content */}
                              <Box
                                style={{
                                  marginBottom: '8px',
                                  padding: 'var(--space-3)',
                                  borderRadius: 'var(--radius-3)',
                                  border: isActive 
                                    ? `2px solid ${formData.primaryColor}` 
                                    : isLocked
                                      ? '1px solid var(--amber-a6)'
                                      : '1px solid var(--gray-a6)',
                                  backgroundColor: isLocked ? 'var(--amber-a2)' : 'var(--color-panel-solid)',
                                  cursor: 'pointer',
                                  opacity: isDragging ? 0.4 : 1,
                                }}
                                onClick={() => {
                                  setCurrentQuestionIndex(index);
                                  if (hasOptions) {
                                    setExpandedBlockId(expandedBlockId === block.id ? null : block.id);
                                  } else {
                                    setExpandedBlockId(null);
                                  }
                                }}
                              >
                                <Flex direction="column" gap="2">
                                    {/* Header row */}
                                    <Flex justify="between" align="center">
                                      <Flex gap="2" align="center">
                                        {/* Drag handle inside the box - hidden for locked blocks */}
                                        {!isLocked ? (
                                          <Flex 
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, block.id)}
                                            onDragEnd={handleDragEnd}
                                            style={{ 
                                              cursor: 'grab', 
                                              color: 'var(--gray-a9)',
                                              padding: '2px',
                                              marginLeft: '-4px',
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <DragHandleDots2Icon width={16} height={16} />
                                          </Flex>
                                        ) : (
                                          <Tooltip content="This question is required and cannot be moved">
                                            <Flex style={{ padding: '2px', marginLeft: '-4px', color: 'var(--amber-9)' }}>
                                              <svg width="16" height="16" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M5 4.63601C5 3.76031 5.24219 3.1054 5.64323 2.67357C6.03934 2.24705 6.64582 1.9783 7.5014 1.9783C8.35745 1.9783 8.96306 2.24652 9.35823 2.67208C9.75838 3.10299 10 3.75708 10 4.63325V5.99999H5V4.63601ZM4 5.99999V4.63601C4 3.58148 4.29339 2.65754 4.91049 1.99307C5.53252 1.32329 6.42675 0.978302 7.5014 0.978302C8.57583 0.978302 9.46952 1.32233 10.091 1.99162C10.7076 2.65557 11 3.57896 11 4.63325V5.99999H12C12.5523 5.99999 13 6.44771 13 6.99999V13C13 13.5523 12.5523 14 12 14H3C2.44772 14 2 13.5523 2 13V6.99999C2 6.44771 2.44772 5.99999 3 5.99999H4ZM3 6.99999H12V13H3V6.99999Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                                              </svg>
                                            </Flex>
                                          </Tooltip>
                                        )}
                                        <Text size="1" color="gray">{index + 1}.</Text>
                                        <Text size="1">{blockConfig?.icon}</Text>
                                        <Text size="2" weight="medium">{blockConfig?.name}</Text>
                                        {isLocked && <Text size="1" color="amber">(Required)</Text>}
                                        {hasOptions && (isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />)}
                                      </Flex>
                                      <Flex gap="1" align="center">
                                        {/* Required toggle - disabled for locked blocks */}
                                        {!isLocked && (
                                          <Tooltip content={block.required ? "Required" : "Optional"}>
                                            <Switch 
                                              size="1" 
                                              checked={block.required}
                                              onCheckedChange={(checked) => updateFormBlock(block.id, { required: checked })}
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          </Tooltip>
                                        )}
                                        {/* Delete button - hidden for locked blocks */}
                                        {!isLocked && (
                                          <IconButton size="1" variant="ghost" color="red" onClick={(e) => { e.stopPropagation(); removeFormBlock(block.id); }}>
                                            <TrashIcon />
                                          </IconButton>
                                        )}
                                      </Flex>
                                    </Flex>
                                    
                                    {/* Question text */}
                                    <TextField.Root 
                                      size="1"
                                      value={block.question}
                                      onChange={(e) => updateFormBlock(block.id, { question: e.target.value })}
                                      placeholder="Enter question..."
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    
                                    {/* Expanded section with options editing */}
                                    {isExpanded && hasOptions && (
                                      <Flex 
                                        direction="column" 
                                        gap="2" 
                                        pt="2" 
                                        style={{ borderTop: '1px solid var(--gray-a6)' }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Text size="1" weight="medium" color="gray">Options</Text>
                                        {block.options.map((option, optionIndex) => (
                                          <Flex key={optionIndex} gap="2" align="center">
                                            <TextField.Root 
                                              size="1"
                                              value={option}
                                              onChange={(e) => updateOptionInBlock(block.id, optionIndex, e.target.value)}
                                              placeholder={`Option ${optionIndex + 1}`}
                                              style={{ flex: 1 }}
                                            />
                                            {block.options.length > 1 && (
                                              <IconButton 
                                                size="1" 
                                                variant="ghost" 
                                                color="gray" 
                                                onClick={() => removeOptionFromBlock(block.id, optionIndex)}
                                              >
                                                <Cross2Icon />
                                              </IconButton>
                                            )}
                                          </Flex>
                                        ))}
                                        <Button 
                                          size="1" 
                                          variant="soft" 
                                          onClick={() => addOptionToBlock(block.id)}
                                          style={{ alignSelf: 'flex-start' }}
                                        >
                                          <PlusIcon /> Add option
                                        </Button>
                                      </Flex>
                                    )}
                                  </Flex>
                              </Box>
                              
                              {/* Drop indicator line - below last item */}
                              {showDropIndicatorBelow && (
                                <Box
                                  style={{
                                    position: 'absolute',
                                    bottom: '7px',
                                    left: 0,
                                    right: 0,
                                    height: '3px',
                                    backgroundColor: '#4285f4',
                                    borderRadius: '2px',
                                    zIndex: 10,
                                  }}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Flex>
                      
                      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px'}}/>
                      
                      {/* Add New Block */}
                      <Flex px="4" direction="column" gap="2" pb="4">
                        <Text size="2" weight="medium">Add block</Text>
                        
                        <Text size="1" color="gray" mt="1">Preset blocks</Text>
                        <Flex gap="2" wrap="wrap">
                          {Object.entries(USER_ADDABLE_BLOCK_TYPES)
                            .filter(([_, config]) => config.preset)
                            .map(([type, config]) => (
                              <Button 
                                key={type} 
                                size="1" 
                                variant="soft"
                                onClick={() => addFormBlock(type)}
                              >
                                {config.icon} {config.name}
                              </Button>
                            ))}
                        </Flex>
                        
                        <Text size="1" color="gray" mt="2">Custom blocks</Text>
                        <Flex gap="2" wrap="wrap">
                          {Object.entries(USER_ADDABLE_BLOCK_TYPES)
                            .filter(([_, config]) => !config.preset)
                            .map(([type, config]) => (
                              <Button 
                                key={type} 
                                size="1" 
                                variant="outline"
                                onClick={() => addFormBlock(type)}
                              >
                                {config.icon} {config.name}
                              </Button>
                            ))}
                        </Flex>
                      </Flex>
                    </Flex>
                  </Tabs.Content>

                  {/* Rewards Tab */}
                  <Tabs.Content value="rewards" key="rewards-tab">
                    <Flex direction="column" gap="4">
                      <Flex p="4" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Text size="1" weight="regular" color="gray">
                          Set up rewards to incentivize survey completion. Customers can spin a wheel to win prizes!
                        </Text>
                      </Flex>
                      <Separator size="4" orientation="horizontal" style={{ minHeight: '1px', marginTop: '-16px' }}/>
                      
                      {/* Enable Rewards Toggle */}
                      <Flex px="4" gap="3" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                        <Flex justify="between" align="center" style={{ width: '100%' }}>
                          <Flex direction="column" gap="1">
                            <Text size="2" weight="medium">Enable rewards</Text>
                            <Text size="1" color="gray">Allow customers to win prizes after completing the survey</Text>
                          </Flex>
                          <Switch 
                            checked={rewardsConfig.enabled} 
                            onCheckedChange={(checked) => {
                              setRewardsConfig(prev => ({ ...prev, enabled: checked }));
                              // Reset wheel preview when disabling rewards
                              if (!checked) {
                                resetWheelPreview();
                              }
                            }}
                          />
                        </Flex>
                        
                        {/* Caution badge when rewards enabled but no campaign duration */}
                        {rewardsConfig.enabled && !hasCampaignDuration && (
                          <Flex p="2" gap="2" align="center" style={{ 
                            width: '100%', 
                            backgroundColor: 'var(--amber-3)',
                            borderRadius: 'var(--radius-2)',
                          }}>
                            <ExclamationTriangleIcon width={14} height={14} style={{ color: 'var(--amber-11)' }} />
                            <Text size="1" color="amber">
                              Rewards won't be active until you set a campaign duration below (date range or "run until stock limit reached")
                            </Text>
                          </Flex>
                        )}
                      </Flex>
                      
                      {rewardsConfig.enabled && (
                        <>
                          <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
                          
                          {/* Date Range */}
                          <Flex px="4" gap="1" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                            <Text size="2" weight="medium">Campaign duration</Text>
                            <Text size="1" color="gray">Set when the rewards campaign is active</Text>
                            
                            <Flex gap="2" align="center" mt="2" style={{ width: '100%' }}>
                              <Switch 
                                checked={rewardsConfig.runUntilLimitReached} 
                                onCheckedChange={(checked) => {
                                  setRewardsConfig(prev => ({ ...prev, runUntilLimitReached: checked }));
                                  // Reset wheel preview when turning off "run until limit" and no date range is set
                                  if (!checked && !(rewardsConfig.dateRange?.from && rewardsConfig.dateRange?.to)) {
                                    resetWheelPreview();
                                  }
                                }}
                              />
                              <Text size="2">Run until stock limit reached (no end date)</Text>
                            </Flex>
                            
                            {!rewardsConfig.runUntilLimitReached && (
                              <Box mt="2" style={{ width: '100%' }}>
                                <DatePickerWithRange
                                  value={rewardsConfig.dateRange}
                                  onDateChange={(range) => {
                                    setRewardsConfig(prev => ({ ...prev, dateRange: range }));
                                    // Reset wheel preview if date range is cleared
                                    if (!range?.from || !range?.to) {
                                      resetWheelPreview();
                                    }
                                  }}
                                  allowFutureDates={true}
                                  disableBeforeDate={new Date()}
                                  numberOfMonths={1}
                                />
                              </Box>
                            )}
                          </Flex>
                          
                          <Separator size="4" orientation="horizontal" style={{ minHeight: '1px' }}/>
                          
                          {/* Rewards List */}
                          <Flex px="4" pb="4" gap="3" direction="column" style={{ alignItems: "flex-start", alignSelf: "stretch" }}>
                            <Flex justify="between" align="center" style={{ width: '100%' }}>
                              <Text size="2" weight="medium">Rewards ({rewards.length})</Text>
                              <Button size="1" variant="soft" onClick={addReward}>
                                <PlusIcon width={14} height={14} /> Add reward
                              </Button>
                            </Flex>
                            
                            {/* Probability warning */}
                            {totalProbability !== 100 && (
                              <Flex p="2" style={{ 
                                width: '100%', 
                                backgroundColor: totalProbability > 100 ? 'var(--red-3)' : 'var(--amber-3)',
                                borderRadius: 'var(--radius-2)',
                              }}>
                                <Text size="1" color={totalProbability > 100 ? 'red' : 'amber'}>
                                  ⚠️ Total probability is {totalProbability}% (should be 100%)
                                </Text>
                              </Flex>
                            )}
                            
                            {/* Rewards cards */}
                            <Flex direction="column" gap="2" style={{ width: '100%' }}>
                              {rewards.map((reward) => (
                                <Card key={reward.id} style={{ width: '100%' }}>
                                  <Flex direction="column" gap="2">
                                    <Flex justify="between" align="start">
                                      <Flex direction="column" gap="1">
                                        <Text size="2" weight="medium">{reward.title}</Text>
                                        <Flex gap="2" wrap="wrap">
                                          <Badge size="1" color="blue">{reward.probability}% chance</Badge>
                                          <Badge size="1" color="gray">{reward.stockLimit - reward.stockUsed}/{reward.stockLimit} left</Badge>
                                        </Flex>
                                      </Flex>
                                      <Flex gap="1">
                                        <IconButton size="1" variant="ghost" onClick={() => openEditReward(reward)}>
                                          <Pencil1Icon width={14} height={14} />
                                        </IconButton>
                                        <IconButton size="1" variant="ghost" color="red" onClick={() => promptDeleteReward(reward)}>
                                          <TrashIcon width={14} height={14} />
                                        </IconButton>
                                      </Flex>
                                    </Flex>
                                    {reward.description && (
                                      <Text size="1" color="gray">{reward.description}</Text>
                                    )}
                                    {reward.terms && (
                                      <Text size="1" color="gray">Terms: {reward.terms}</Text>
                                    )}
                                    <Flex gap="1" align="center">
                                      <Text size="1" color="gray">Code:</Text>
                                      <Badge size="1" variant="outline">{reward.couponCode || 'No code set'}</Badge>
                                    </Flex>
                                  </Flex>
                                </Card>
                              ))}
                            </Flex>
                            
                            {rewards.length === 0 && (
                              <Flex p="4" justify="center" style={{ width: '100%', backgroundColor: 'var(--gray-2)', borderRadius: 'var(--radius-2)' }}>
                                <Text size="2" color="gray">No rewards added yet. Click "Add Reward" to create one.</Text>
                              </Flex>
                            )}
                          </Flex>
                        </>
                      )}
                    </Flex>
                  </Tabs.Content>
                </Tabs.Root>
              </Flex>

              {/* Save changes */}
              {!isNewTemplate && (
                <>
                  <Separator size="4" orientation="horizontal" style={{ minHeight: "1px" }} />
                  <Flex p="4" gap="2" style={{ alignSelf: "stretch" }}>
                    <Button
                      variant="soft"
                      color="red"
                      disabled={!hasUnsavedChanges || isUpdating}
                      onClick={handleDiscard}
                      style={{ flex: 1 }}
                    >
                      Discard changes
                    </Button>
                    <Button
                      variant="solid"
                      onClick={handleSave}
                      disabled={!hasUnsavedChanges || !formData.name.trim() || isUpdating}
                      style={{ flex: 1 }}
                    >
                      {isUpdating ? 'Saving...' : 'Save changes'}
                    </Button>
                  </Flex>
                </>
              )}
            </Flex>
          </Flex>

          {/* Right Panel - Preview */}
          <Flex direction="column"
            style={{ alignItems: "stretch", alignSelf: "stretch", flex: "1 0 0", borderRadius: "var(--radius-4)",
              overflow: "hidden", border: "1px solid var(--gray-a6)",
              backgroundColor: "var(--gray-a3)",
            }}>
            {/* Preview Container */}
            <Box style={{ padding: 20, overflow: "hidden", flex: "1 1 0", display: "flex", justifyContent: "center", 
              alignItems: "flex-start" }}>
              <Flex 
                direction="column"
                align="center"
                style={{ 
                  width: view === "mobile" ? "375px" : "100%",
                  maxWidth: '100%',
                  height: '100%',
                  overflow: "hidden",
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                  transition: "width 0.3s ease",
                  position: "relative",
                  background: !backgroundPreview
                    ? `linear-gradient(rgba(0,0,0,0.11), rgba(0,0,0,0.11)), ${formData.primaryColor}`
                    : undefined,
                  borderRadius: "var(--radius-4)",
                }}
              >
                {/* Background layer with blur effect */}
                {backgroundPreview && (
                  <>
                    {/* Blurred background image */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        backgroundImage: `url(${backgroundPreview})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                        filter: `blur(${(formData.backgroundBlurAmount / 100) * 20}px)`,
                        // Scale up slightly to prevent blur edge artifacts
                        transform: formData.backgroundBlurAmount > 0 ? "scale(1.1)" : undefined,
                        zIndex: 0,
                      }}
                    />
                    {/* Overlay on top of blurred background */}
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: colorConversion(formData.backgroundOverlayColor, formData.backgroundOverlayOpacity),
                        zIndex: 1,
                      }}
                    />
                  </>
                )}
                {/* Scrollable Content */}
                <Flex 
                  direction="column" 
                  align="center" 
                  justify="between"
                  gap="4" 
                  p="5"
                  style={{ 
                    width: '100%', 
                    height: '100%',
                    flex: 1,
                    position: "relative",
                    zIndex: 2,
                    overflowY: "auto",
                    overflowX: "hidden",
                    opacity: viewTransitioning ? 0 : 1,
                    transition: 'opacity 0.15s ease',
                  }}
                >
                  {/* Progress bar - only show on question pages */}
                  {!isOnIntroPage && !isOnThankYouPage && formBlocks.length > 0 && (
                    <Flex direction="column" gap="2" style={{ width: '100%', maxWidth: view === 'mobile' ? '320px' : '480px', padding: '0 16px' }}>
                      {/* Question counter and percentage */}
                      <Flex justify="between" align="center" style={{ width: '100%' }}>
                        <Text size="2" weight="medium" style={{ color: formData.textColor }}>
                          Question {currentQuestionIndex + 1}/{formBlocks.length}
                        </Text>
                        <Text size="2" weight="medium" style={{ color: formData.textColor }}>
                          {Math.round(((currentQuestionIndex + 1) / formBlocks.length) * 100)}%
                        </Text>
                      </Flex>
                      {/* Progress bar */}
                      <Box 
                        style={{ 
                          width: '100%', 
                          height: '8px', 
                          backgroundColor: 'rgba(255,255,255,0.2)', 
                          borderRadius: '4px',
                          overflow: 'hidden',
                        }}
                      >
                        <Box 
                          style={{ 
                            width: `${((currentQuestionIndex + 1) / formBlocks.length) * 100}%`, 
                            height: '100%', 
                            backgroundColor: formData.primaryColor,
                            borderRadius: '4px',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                    </Flex>
                  )}

                  {/* Page Content */}
                  <Flex 
                    direction="column" 
                    align="center" 
                    justify="center" 
                    gap="5"
                    style={{ flex: 1, width: '100%' }}
                  >
                    {/* Logo - only on intro and thank you pages, positioned above content */}
                    {logoPreview && (isOnIntroPage || isOnThankYouPage) && (
                      <Flex 
                        style={{ 
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '200px',
                          height: '200px',
                        }}
                      >
                        <img 
                          src={logoPreview} 
                          alt="Logo" 
                          style={{ 
                            objectFit: 'contain', 
                            maxWidth: '100%',
                            maxHeight: '100%',
                            borderRadius: 'var(--radius-3)',
                          }}
                        />
                      </Flex>
                    )}
                  
                    {isOnIntroPage ? (
                      // Intro page
                      <Flex direction="column" align="center" gap="4" style={{ maxWidth: view === 'mobile' ? '320px' : '480px', textAlign: 'center' }}>
                        <Text size="6" weight="bold" align="center" style={{ color: formData.textColor }}>
                          {formData.introTitle}
                        </Text>
                        {previewIntroSubtitle ? (
                          <Text size="3" align="center" style={{ color: formData.textColor, opacity: 0.9 }}>
                            {previewIntroSubtitle}
                          </Text>
                        ) : null}
                        {showRewardPromoInIntro ? (
                          <Text size="3" align="center" style={{ color: formData.textColor, opacity: 0.9 }}>
                            {REWARD_PROMO_TEXT}
                          </Text>
                        ) : null}
                        {showRewardPromoInIntro ? (
                          <Text size="1" align="center" style={{ color: formData.textColor, opacity: 0.78 }}>
                            **While supplies last.
                          </Text>
                        ) : null}
                        <Flex align="center" gap="2" style={{ opacity: 0.7 }}>
                          <ClockIcon width={14} height={14} style={{ color: formData.textColor }} />
                          <Text size="2" style={{ color: formData.textColor }}>
                            {formData.introTimeEstimate}
                          </Text>
                        </Flex>
                      </Flex>
                    ) : isOnThankYouPage ? (
                      // Thank you page with rewards
                      <Flex
                        direction="column"
                        align="center"
                        gap="4"
                        style={{
                          width: '100%',
                          maxWidth: view === 'mobile' ? '95vw' : '400px',
                        }}
                      >
                        {/* Reward Won Popup */}
                        {showRewardPopup && wheelResult && (
                          <Flex 
                            direction="column" 
                            align="center" 
                            gap="4" 
                            px="5"
                            py="5"
                            style={{ 
                              width: view === 'mobile' ? '100%' : '400px',
                              backgroundColor: 'rgba(255,255,255,0.98)',
                              borderRadius: 'var(--radius-4)',
                              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                              textAlign: 'center',
                            }}
                          >
                            <Text size="8">🎉</Text>
                            <Text size="5" weight="bold" style={{ color: '#1f2937' }}>
                              You won
                            </Text>
                            <Text size="4" weight="bold" style={{ color: formData.primaryColor }}>
                              {wheelResult.title}
                            </Text>
                            {wheelResult.description ? (
                              <Text size="2" style={{ color: '#6b7280' }}>
                                {wheelResult.description}
                              </Text>
                            ) : null}
                            {wheelResult.terms ? (
                              <Text size="1" style={{ color: '#6b7280' }}>
                                Terms: {wheelResult.terms}
                              </Text>
                            ) : null}
                            <Flex
                              align="center"
                              justify="center"
                              mt="2"
                              px="4"
                              py="3"
                              style={{
                                backgroundColor: `${formData.primaryColor}15`,
                                borderRadius: 'var(--radius-3)',
                                border: `1px solid ${formData.primaryColor}35`,
                                width: '100%',
                              }}
                            >
                              <Text size="3" weight="bold" style={{ color: formData.primaryColor }}>
                                Reward sent to email!
                              </Text>
                            </Flex>
                          </Flex>
                        )}
                        
                        {/* Spinning Wheel */}
                        {showWheelPreview && !showRewardPopup && (
                          <Flex 
                            direction="column" 
                            align="center" 
                            gap="4"
                            p={view === 'mobile' ? '3' : '6'}
                            style={{ 
                              // WHEEL CARD SIZE: Adjust width for mobile/desktop here
                              width: view === 'mobile' ? '95%' : '480px',
                              maxWidth: '480px',
                              backgroundColor: 'rgba(255,255,255,0.98)',
                              borderRadius: 'var(--radius-4)',
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                            }}
                          >
                            <Text size="4" weight="bold" style={{ color: '#1f2937' }}>
                              {wheelSpinning ? 'Spinning...' : 'Tap to spin!'}
                            </Text>
                            <Text size="2" align="center" style={{ color: '#6b7280', maxWidth: '320px' }}>
                              Reward will automatically be sent to your email after spinning the wheel.
                            </Text>
                            
                            {/* Wheel Container 
                                WHEEL SIZE: Change the width/height values below to resize the wheel
                                - Mobile: currently 260px
                                - Desktop: currently 360px
                            */}
                            <Box 
                              onClick={(!wheelSpinning && !wheelResult) ? spinWheel : undefined}
                              style={{ 
                                position: 'relative',
                                width: view === 'mobile' ? '260px' : '360px',
                                height: view === 'mobile' ? '260px' : '360px',
                                cursor: (wheelSpinning || wheelResult) ? 'default' : 'pointer',
                              }}
                            >
                              {/* Pointer */}
                              <Box style={{
                                position: 'absolute',
                                top: '-12px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                zIndex: 10,
                                fontSize: '24px',
                                color: formData.primaryColor,
                              }}>
                                ▼
                              </Box>
                              
                              {/* Wheel */}
                              <svg 
                                viewBox="0 0 200 200" 
                                style={{ 
                                  width: '100%', 
                                  height: '100%',
                                  transform: `rotate(${wheelRotation}deg)`,
                                  transition: wheelSpinning ? 'transform 3s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
                                }}
                              >
                                {wheelDisplayRewards.map((reward, index) => {
                                  const segmentAngle = 360 / wheelDisplayRewards.length;
                                  const startAngle = index * segmentAngle - 90;
                                  const endAngle = startAngle + segmentAngle;
                                  const startRad = (startAngle * Math.PI) / 180;
                                  const endRad = (endAngle * Math.PI) / 180;
                                  const x1 = 100 + 90 * Math.cos(startRad);
                                  const y1 = 100 + 90 * Math.sin(startRad);
                                  const x2 = 100 + 90 * Math.cos(endRad);
                                  const y2 = 100 + 90 * Math.sin(endRad);
                                  const largeArc = segmentAngle > 180 ? 1 : 0;
                                  
                                  // Generate vibrant colors based on primary color
                                  const wheelColors = generateWheelColors(formData.primaryColor, wheelDisplayRewards.length);
                                  const color = reward.wheelColor || wheelColors[index];
                                  
                                  // Text position
                                  const midAngle = startAngle + segmentAngle / 2;
                                  const midRad = (midAngle * Math.PI) / 180;
                                  const textX = 100 + 55 * Math.cos(midRad);
                                  const textY = 100 + 55 * Math.sin(midRad);
                                  
                                  return (
                                    <g key={reward._displayId || `${reward.id}-${index}`}>
                                      <path
                                        d={`M 100 100 L ${x1} ${y1} A 90 90 0 ${largeArc} 1 ${x2} ${y2} Z`}
                                        fill={color}
                                        stroke="#fff"
                                        strokeWidth="2"
                                      />
                                      <text
                                        x={textX}
                                        y={textY}
                                        fill="#fff"
                                        fontSize="8"
                                        fontWeight="bold"
                                        textAnchor="middle"
                                        dominantBaseline="middle"
                                        transform={`rotate(${midAngle + 90}, ${textX}, ${textY})`}
                                        style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                                      >
                                        {reward.title.length > 12 ? reward.title.substring(0, 12) + '...' : reward.title}
                                      </text>
                                    </g>
                                  );
                                })}
                                {/* Center circle */}
                                <circle cx="100" cy="100" r="20" fill="#fff" stroke={formData.primaryColor} strokeWidth="3" />
                                <text x="100" y="100" fill={formData.primaryColor} fontSize="8" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                                  SPIN
                                </text>
                              </svg>
                            </Box>
                          </Flex>
                        )}
                        
                        {/* Normal Thank You (with claim reward button if rewards enabled) */}
                        {!showWheelPreview && !showRewardPopup && (
                          <>
                            <Text size="5" weight="bold" align="center" style={{ color: formData.textColor }}>
                              {formData.thankYouMessage}
                            </Text>
                            
                            {/* Claim Reward Section - only show if rewards enabled AND campaign duration is set */}
                            {shouldShowRewardsInPreview && (
                              <Flex 
                                direction="column" 
                                align="center" 
                                gap="3" 
                                mt="4"
                                p={view === 'mobile' ? '4' : '5'}
                                style={{ 
                                  width: view === 'mobile' ? '100%' : '460px',
                                  maxWidth: '460px',
                                  backgroundColor: 'rgba(255,255,255,0.98)',
                                  borderRadius: 'var(--radius-4)',
                                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                                }}
                              >
                                <Text size="3" align="center" weight="medium" style={{ color: '#1f2937' }}>
                                  Now spin to reveal your reward!
                                </Text>
                                <Button 
                                  size="4"
                                  style={{ 
                                    backgroundColor: formData.primaryColor,
                                    color: '#fff',
                                    padding: view === 'mobile' ? '16px 20px' : '14px 36px',
                                    fontSize: '16px',
                                    lineHeight: 1.3,
                                  }}
                                  onClick={() => setShowWheelPreview(true)}
                                >
                                  🎁 Claim Your Reward
                                </Button>
                                <Text size="1" style={{ color: '#6b7280' }}>
                                  (Limit one per user)
                                </Text>
                              </Flex>
                            )}
                          </>
                        )}
                      </Flex>
                    ) : formBlocks.length === 0 ? (
                      // No questions
                      <Text size="3" align="center" style={{ color: formData.textColor, opacity: 0.7 }}>
                        Add form blocks to preview your survey
                      </Text>
                    ) : (
                      // Current question
                      renderQuestionBlock(currentBlock)
                    )}
                  </Flex>

                  {/* Navigation Buttons */}
                  <Flex gap="3" style={{ width: '90%', maxWidth: view === 'mobile' ? '320px' : '480px' }}>
                    {isOnIntroPage ? (
                      // Begin button on intro page
                      <Button 
                        size="3"
                        style={{ 
                          flex: 1,
                          backgroundColor: formData.primaryColor,
                          color: '#fff',
                        }}
                        onClick={handleNextQuestion}
                      >
                        Begin
                      </Button>
                    ) : (
                      <>
                        {canGoPrev && (
                          <Button 
                            size="3"
                            variant="soft"
                            style={{ 
                              flex: 1,
                              backgroundColor: 'rgba(255,255,255,0.2)',
                              color: formData.textColor,
                            }}
                            onClick={handlePrevQuestion}
                          >
                            <ChevronLeftIcon /> Back
                          </Button>
                        )}
                        {!isOnThankYouPage && formBlocks.length > 0 && (
                          <Button 
                            size="3"
                            style={{ 
                              flex: 1,
                              backgroundColor: formData.primaryColor,
                              color: '#fff',
                            }}
                            onClick={handleNextQuestion}
                          >
                            {currentQuestionIndex === formBlocks.length - 1 ? 'Submit' : 'Next'} <ChevronRightIcon />
                          </Button>
                        )}
                      </>
                    )}
                  </Flex>

                  {isOnIntroPage ? (
                    <Text size="1" align="center" style={{ color: formData.textColor, opacity: 0.92, maxWidth: view === 'mobile' ? '320px' : '480px' }}>
                      This survey is powered by bconnect. By continuing, you agree to bconnect&apos;s{' '}
                      <a
                        href="/surveys/terms"
                        onClick={(event) => event.preventDefault()}
                        style={{ color: formData.textColor, textDecoration: 'underline' }}
                      >
                        Terms and Conditions
                      </a>
                      {' '}and{' '}
                      <a
                        href="/surveys/privacy"
                        onClick={(event) => event.preventDefault()}
                        style={{ color: formData.textColor, textDecoration: 'underline' }}
                      >
                        Privacy Policy
                      </a>
                    </Text>
                  ) : null}
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </Flex>
        
        <AlertDialog.Root open={discardAlertOpen} onOpenChange={setDiscardAlertOpen}>
          <AlertDialog.Content maxWidth="450px">
            <AlertDialog.Title>Discard changes?</AlertDialog.Title>
            <AlertDialog.Description size="2">
              Are you sure you want to discard all unsaved changes? This action cannot be undone.
            </AlertDialog.Description>

            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">Cancel</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button variant="solid" color="red" onClick={confirmDiscard}>Discard changes</Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>

        <AlertDialog.Root
          open={backAlertOpen}
          onOpenChange={(open) => {
            setBackAlertOpen(open);
            if (!open) {
              browserBackPendingRef.current = false;
            }
          }}
        >
          <AlertDialog.Content maxWidth="450px">
            <AlertDialog.Title>Leave without saving?</AlertDialog.Title>
            <AlertDialog.Description size="2">
              You have unsaved changes. Are you sure you want to leave? Your changes will be lost.
            </AlertDialog.Description>

            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">Cancel</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button variant="solid" color="red" onClick={confirmBack}>Leave without saving</Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>

        {/* Reward Edit Dialog */}
        <Dialog.Root open={rewardDialogOpen} onOpenChange={(open) => {
          setRewardDialogOpen(open);
          // Clear editing state when dialog closes (cancel)
          if (!open) {
            setEditingReward(null);
            setIsNewReward(false);
          }
        }}>
          <Dialog.Content style={{ maxWidth: '450px' }}>
            <Dialog.Title>
              {isNewReward ? 'Add Reward' : 'Edit Reward'}
            </Dialog.Title>
            <Dialog.Description size="2" color="gray">
              Configure the details for this reward.
            </Dialog.Description>
            
            {editingReward && (
              <Flex direction="column" gap="4" mt="4">
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Title <span style={{ color: 'var(--red-9)' }}>*</span></Text>
                  <TextField.Root 
                    placeholder="e.g., 10% Off Coupon"
                    value={editingReward.title}
                    onChange={(e) => setEditingReward(prev => ({ ...prev, title: e.target.value }))}
                  />
                </Flex>
                
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Coupon Code <span style={{ color: 'var(--red-9)' }}>*</span></Text>
                  <TextField.Root 
                    placeholder="e.g., SAVE10"
                    value={editingReward.couponCode}
                    onChange={(e) => setEditingReward(prev => ({ ...prev, couponCode: e.target.value.toUpperCase() }))}
                    style={{ fontFamily: 'monospace' }}
                  />
                </Flex>
                
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Description</Text>
                  <TextArea 
                    placeholder="Describe what the customer wins..."
                    value={editingReward.description}
                    onChange={(e) => setEditingReward(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                  />
                </Flex>

                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Terms</Text>
                  <TextArea 
                    placeholder="Optional reward terms shown in customer email..."
                    value={editingReward.terms || ''}
                    onChange={(e) => setEditingReward(prev => ({ ...prev, terms: e.target.value }))}
                    rows={3}
                  />
                </Flex>
                
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Probability</Text>
                  <Text size="1" color="gray">Chance of winning this reward</Text>
                  <Flex gap="2" align="center">
                    <input
                      type="range"
                      min={1}
                      max={100}
                      step={1}
                      value={editingReward.probability}
                      onChange={(e) => setEditingReward(prev => ({ ...prev, probability: parseInt(e.target.value) || 1 }))}
                      style={{ flex: 1 }}
                    />
                    <TextField.Root
                      size="2"
                      type="number"
                      min={0}
                      value={editingReward.probability}
                      onChange={(e) => setEditingReward(prev => ({ ...prev, probability: e.target.value }))}
                      style={{ textAlign: "center", width: "70px" }}
                    >
                      <TextField.Slot side="right" style={{ padding: "4px" }}>
                        %
                      </TextField.Slot>
                    </TextField.Root>
                  </Flex>
                </Flex>

                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Wheel Color</Text>
                  <Text size="1" color="gray">Custom color for this segment on the wheel (leave empty for auto)</Text>
                  <Flex gap="2" align="center">
                    <input
                      type="color"
                      value={editingReward.wheelColor || '#ffffff'}
                      onChange={(e) => setEditingReward(prev => ({ ...prev, wheelColor: e.target.value }))}
                      style={{ width: 36, height: 36, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 'var(--radius-2)' }}
                    />
                    <TextField.Root
                      size="2"
                      placeholder="#ffffff"
                      value={editingReward.wheelColor || ''}
                      onChange={(e) => setEditingReward(prev => ({ ...prev, wheelColor: e.target.value }))}
                      style={{ flex: 1 }}
                    />
                    {editingReward.wheelColor && (
                      <Button
                        variant="soft"
                        color="gray"
                        size="1"
                        onClick={() => setEditingReward(prev => ({ ...prev, wheelColor: '' }))}
                      >
                        Reset
                      </Button>
                    )}
                  </Flex>
                </Flex>
                
                <Flex direction="column" gap="1">
                  <Text size="2" weight="medium">Stock Limit <span style={{ color: 'var(--red-9)' }}>*</span></Text>
                  <Text size="1" color="gray">Total number of this reward available</Text>
                  <TextField.Root 
                    type="number"
                    min="0"
                    placeholder="e.g., 25"
                    value={editingReward.stockLimit || ''}
                    onChange={(e) => setEditingReward(prev => ({ ...prev, stockLimit: parseInt(e.target.value) || 0 }))}
                  />
                </Flex>
              </Flex>
            )}
            
            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button 
                onClick={saveEditingReward}
                disabled={!editingReward?.title || !editingReward?.couponCode || !editingReward?.stockLimit}
              >
                Save Reward
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog.Root>

        <AlertDialog.Root
          open={rewardDeleteConfirmOpen}
          onOpenChange={(open) => {
            setRewardDeleteConfirmOpen(open);
            if (!open) {
              setRewardToDelete(null);
            }
          }}
        >
          <AlertDialog.Content maxWidth="450px">
            <AlertDialog.Title>Delete reward code</AlertDialog.Title>
            <AlertDialog.Description size="2">
              {rewardToDelete
                ? `Are you sure you want to delete "${rewardToDelete.title}"?`
                : 'Are you sure you want to delete this reward?'}
            </AlertDialog.Description>

            <Flex gap="3" mt="4" justify="end">
              <AlertDialog.Cancel>
                <Button variant="soft" color="gray">Cancel</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action>
                <Button variant="solid" color="red" onClick={confirmDeleteReward}>Delete</Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      </Flex>
      <ToastAlert />
    </>
  );
}
